import { CanvasRenderer } from './CanvasRenderer';
import type { ViewState } from './CanvasRenderer';
import { TextRenderer } from './TextRenderer';
import { SharedState } from '../worker/SharedState';
import { ViewportStreamer } from '../data/ViewportStreamer';
import { perf } from '../perf/devPerf';
import { formatFormulaRangeRef, type FormulaRangeCoord } from '../spreadsheet/formulaAuthoring';
import { cellTextMatchesQuery } from '../spreadsheet/findReplace';
import {
    buildCreateTableFromSelectionPlan,
    buildDeleteTablePlan,
    buildRenameTablePlan,
    buildResizeTableToSelectionPlan,
    buildSetTableStylePlan,
    buildToggleTableFilterButtonsPlan,
    buildToggleTableHeaderRowPlan,
    buildToggleTableTotalRowPlan,
    type TableMutationPlan,
} from '../spreadsheet/autoFilterUi';
import {
    buildDeleteDerivedArtifactPlan,
    buildDerivedArtifactInsertPlan,
    buildExcelRangeChartInsertPlan,
    buildMoveDerivedArtifactSourceToCellPlan,
    buildMoveDerivedArtifactToCellPlan,
    buildRenameDerivedArtifactPlan,
    buildResizeDerivedArtifactSourceToCellPlan,
    buildResizeDerivedArtifactToCellPlan,
    buildResizeDerivedArtifactToSelectionPlan,
    buildSetDerivedArtifactChartMetadataPlan,
    buildSetDerivedArtifactChartTypePlan,
    buildSetDerivedArtifactSourceRangeDraftPlan,
    buildSetDerivedArtifactSourceToSelectionPlan,
    type ExcelRangeChartMetadataUpdate,
    type DerivedArtifactMutationPlan,
} from '../spreadsheet/derivedArtifactPreview';
import {
    resolveSpreadsheetGridKeyCommand,
    type SpreadsheetGridCommandDecision,
} from '../spreadsheet/spreadsheetGridCommands';
import type { SpreadsheetFindMatch } from '../data/ViewportStreamer';
import type {
    SpreadsheetFillRangeMode,
    SpreadsheetPasteSpecialMode,
    SpreadsheetConditionalFormatRule,
    SpreadsheetAutoFilter,
    SpreadsheetChartArtifactType,
    SpreadsheetDerivedArtifact,
    SpreadsheetRangeAddress,
    SpreadsheetSparklineGroup,
    SpreadsheetTableDefinition,
    SpreadsheetValidationRule,
} from '../protocol/spreadsheetEngineProtocol';

export const GRID_CONTEXT_MENU_OWNER_ATTRIBUTE = 'data-fg-grid-context-menu-owner';
export const GRID_CONTEXT_MENU_OWNER_SELECTOR = `[${GRID_CONTEXT_MENU_OWNER_ATTRIBUTE}="1"]`;

export interface CellFormat {
    [key: string]: unknown;
    numberFormat?: {
        category?: string;
        builtInId?: number;
        builtInName?: string;
        code?: string;
        locale?: string;
        decimalPlaces?: number;
        useThousandsSeparator?: boolean;
        currencySymbol?: string;
        negativeNumbers?: string;
    };
    font?: Record<string, unknown>;
    fill?: Record<string, unknown>;
    border?: Record<string, unknown>;
    alignment?: Record<string, unknown>;
    protection?: Record<string, unknown>;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean | string;
    strikethrough?: boolean;
    align?: 'general' | 'left' | 'center' | 'right' | 'fill' | 'justify' | 'centerContinuous' | 'distributed';
    verticalAlign?: 'top' | 'middle' | 'center' | 'bottom' | 'justify' | 'distributed';
    wrapText?: boolean;
    shrinkToFit?: boolean;
    indent?: number;
    textRotation?: number;
    fontSize?: number;
    fontFamily?: string;
    textColor?: string;
    backgroundColor?: string;
    mergeAnchorRowOffset?: number;
    mergeAnchorColOffset?: number;
    mergeRowSpan?: number;
    mergeColSpan?: number;
}

const STYLE_SECTION_KEYS: Array<keyof CellFormat> = [
    'numberFormat',
    'font',
    'fill',
    'border',
    'alignment',
    'protection',
];

const CODELESS_NUMBER_FORMAT_CATEGORIES = new Set([
    'general',
    'number',
    'currency',
    'accounting',
    'percentage',
    'percent',
    'scientific',
    'text',
]);

function shouldReplaceNumberFormatSection(baseValue: unknown, patchValue: unknown): boolean {
    if (
        !patchValue ||
        typeof patchValue !== 'object' ||
        Array.isArray(patchValue)
    ) return false;
    const patch = patchValue as Record<string, unknown>;
    const category = typeof patch.category === 'string' ? patch.category.trim().toLowerCase() : '';
    if (!category) return false;
    if (
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
    ) {
        const baseCategory = typeof (baseValue as Record<string, unknown>).category === 'string'
            ? String((baseValue as Record<string, unknown>).category).trim().toLowerCase()
            : '';
        if (baseCategory && baseCategory !== category) return true;
    }
    return !Object.prototype.hasOwnProperty.call(patch, 'code') && CODELESS_NUMBER_FORMAT_CATEGORIES.has(category);
}

function isGridContextMenuOwnedByOverlay(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest(GRID_CONTEXT_MENU_OWNER_SELECTOR));
}

function mergeCellFormat(base: CellFormat | undefined | null, patch: Partial<CellFormat>): CellFormat {
    const merged: CellFormat = { ...(base || {}), ...patch };
    for (const key of STYLE_SECTION_KEYS) {
        const baseValue = base?.[key];
        const patchValue = patch[key];
        if (
            baseValue &&
            patchValue &&
            typeof baseValue === 'object' &&
            typeof patchValue === 'object' &&
            !Array.isArray(baseValue) &&
            !Array.isArray(patchValue)
        ) {
            if (key === 'numberFormat' && shouldReplaceNumberFormatSection(baseValue, patchValue)) {
                merged[key] = { ...(patchValue as Record<string, unknown>) } as never;
                continue;
            }
            merged[key] = {
                ...(baseValue as Record<string, unknown>),
                ...(patchValue as Record<string, unknown>),
            } as never;
        }
    }
    return merged;
}

const TABLE_HEADER_FORMAT: CellFormat = {
    bold: true,
    textColor: '#0f5132',
    backgroundColor: '#dff3e7',
    fill: { foregroundColor: '#dff3e7' },
    border: {
        top: { style: 'thin', color: '#107c41' },
        bottom: { style: 'thin', color: '#107c41' },
    },
};

const TABLE_BODY_FORMAT: CellFormat = {
    border: {
        bottom: { style: 'hair', color: '#d6e7dc' },
    },
};

const TABLE_BANDED_BODY_FORMAT: CellFormat = {
    backgroundColor: '#f4fbf7',
    fill: { foregroundColor: '#f4fbf7' },
};

const TABLE_TOTAL_ROW_FORMAT: CellFormat = {
    bold: true,
    backgroundColor: '#eef7f1',
    fill: { foregroundColor: '#eef7f1' },
    border: {
        top: { style: 'thin', color: '#107c41' },
        bottom: { style: 'thin', color: '#107c41' },
    },
};

type TableStyleVisual = {
    header: CellFormat;
    body: CellFormat;
    banded?: CellFormat;
    total: CellFormat;
};

const TABLE_STYLE_VISUALS: Record<string, TableStyleVisual> = {
    TableStyleMedium2: {
        header: TABLE_HEADER_FORMAT,
        body: TABLE_BODY_FORMAT,
        banded: TABLE_BANDED_BODY_FORMAT,
        total: TABLE_TOTAL_ROW_FORMAT,
    },
    TableStyleLight1: {
        header: {
            bold: true,
            textColor: '#1f2937',
            backgroundColor: '#f8fafc',
            fill: { foregroundColor: '#f8fafc' },
            border: {
                top: { style: 'thin', color: '#cbd5e1' },
                bottom: { style: 'thin', color: '#94a3b8' },
            },
        },
        body: {
            border: {
                bottom: { style: 'hair', color: '#e2e8f0' },
            },
        },
        banded: {
            backgroundColor: '#f8fafc',
            fill: { foregroundColor: '#f8fafc' },
        },
        total: {
            bold: true,
            backgroundColor: '#f1f5f9',
            fill: { foregroundColor: '#f1f5f9' },
            border: {
                top: { style: 'thin', color: '#64748b' },
                bottom: { style: 'thin', color: '#cbd5e1' },
            },
        },
    },
    TableStyleMedium4: {
        header: {
            bold: true,
            textColor: '#ffffff',
            backgroundColor: '#2f855a',
            fill: { foregroundColor: '#2f855a' },
            border: {
                top: { style: 'thin', color: '#276749' },
                bottom: { style: 'thin', color: '#276749' },
            },
        },
        body: {
            border: {
                bottom: { style: 'hair', color: '#cfe3d5' },
            },
        },
        banded: {
            backgroundColor: '#edf7ef',
            fill: { foregroundColor: '#edf7ef' },
        },
        total: {
            bold: true,
            textColor: '#0f5132',
            backgroundColor: '#d9f0df',
            fill: { foregroundColor: '#d9f0df' },
            border: {
                top: { style: 'thin', color: '#2f855a' },
                bottom: { style: 'thin', color: '#2f855a' },
            },
        },
    },
};

const tableStyleVisualFor = (styleId: string | null | undefined): TableStyleVisual => {
    const normalized = String(styleId ?? '').trim();
    return TABLE_STYLE_VISUALS[normalized] ?? TABLE_STYLE_VISUALS.TableStyleMedium2;
};

const cloneTableDefinition = (table: SpreadsheetTableDefinition): SpreadsheetTableDefinition => ({
    ...table,
    range: { ...table.range },
    columns: table.columns?.map((column) => ({ ...column })),
});

const cloneDerivedArtifact = (artifact: SpreadsheetDerivedArtifact): SpreadsheetDerivedArtifact => ({
    ...artifact,
    source_range: { ...artifact.source_range },
    placement: { ...artifact.placement },
    pivot: artifact.pivot ? {
        ...artifact.pivot,
        row_fields: artifact.pivot.row_fields ? [...artifact.pivot.row_fields] : undefined,
        column_fields: artifact.pivot.column_fields ? [...artifact.pivot.column_fields] : undefined,
        value_fields: artifact.pivot.value_fields ? [...artifact.pivot.value_fields] : undefined,
        filter_fields: artifact.pivot.filter_fields ? [...artifact.pivot.filter_fields] : undefined,
    } : artifact.pivot,
});

export type GridFormulaCellCoord = {
    col: number;
    row: number;
};

export type GridFormulaRangeCoord = {
    start: GridFormulaCellCoord;
    end: GridFormulaCellCoord;
    sheetName?: string | null;
};

export type GridFormulaRangeSelectionPhase = 'start' | 'update' | 'commit';

export type GridFormulaRangeSelectionRequest = {
    phase: GridFormulaRangeSelectionPhase;
    range: GridFormulaRangeCoord;
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
};

export type GridFormulaRangeSelectionHandler = (request: GridFormulaRangeSelectionRequest) => boolean;

export type GridScreenRect = {
    x: number;
    y: number;
    w: number;
    h: number;
};

type GridSelectionRange = { col: number, row: number, endCol: number, endRow: number };
type ClipboardVisualMode = 'copy' | 'cut';
type DragVisualMode = 'move' | 'copy' | 'fill';
export type PasteOptionsContext = {
    targetCol: number;
    targetRow: number;
    rowCount: number;
    colCount: number;
    canPasteSpecial: boolean;
};

export type FillOptionsContext = {
    sourceCol: number;
    sourceRow: number;
    sourceWidth: number;
    sourceHeight: number;
    targetCol: number;
    targetRow: number;
    targetWidth: number;
    targetHeight: number;
    canFillSeries: boolean;
};

export type CellMetadataProjection = {
    validationId?: string;
    comment?: string;
    hyperlink?: string;
    protection?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
};

export type CellProtectionVisualState = 'explicit-locked' | 'explicit-unlocked' | 'sheet-locked' | null;

export type SheetProtectionProjection = {
    enabled?: boolean;
    allow_select_locked_cells?: boolean;
    allow_select_unlocked_cells?: boolean;
    allow_format_cells?: boolean;
    allow_resize_rows?: boolean;
    allow_resize_columns?: boolean;
    allow_insert_rows?: boolean;
    allow_insert_columns?: boolean;
    allow_delete_rows?: boolean;
    allow_delete_columns?: boolean;
} | null;

export type CellHyperlinkActivationContext = {
    col: number;
    row: number;
    hyperlink: string;
};

export class GridController {
    private static readonly ROW_OFFSET_CACHE_LIMIT = 200_000;
    private static readonly COL_OFFSET_CACHE_LIMIT = 10_000;
    private static focusedController: GridController | null = null;
    private renderer: CanvasRenderer | null = null;
    private canvas: HTMLCanvasElement | null = null;
    public streamer: ViewportStreamer | null = null;
    private sharedState: SharedState | null = null;
    private textRenderer: TextRenderer | null = null; // This property was not removed in the instruction snippet, so it remains.

    // UI State
    private scrollX: number = 0;
    private scrollY: number = 0;
    private zoom: number = 1.0;
    private gridlinesVisible: boolean = true;
    private viewportWidth: number = 0;
    private viewportHeight: number = 0;
    private sheetProtectionProjection: SheetProtectionProjection = null;
    private autoFilterProjection: SpreadsheetAutoFilter | null = null;
    private tableProjection: Record<string, SpreadsheetTableDefinition> = {};
    private derivedArtifactProjection: Record<string, SpreadsheetDerivedArtifact> = {};

    // Interaction State
    private isDragging: boolean = false;
    private isEditing: boolean = false;
    private dragMode: 'none' | 'move' | 'copy' | 'fill' | 'formula-range' = 'none';
    private dragStart: { x: number, y: number, col: number, row: number } | null = null;
    private dragCurrent: { x: number, y: number, col: number, row: number } | null = null;
    private clipboardVisual: { mode: ClipboardVisualMode; selection: GridSelectionRange; startedAtMs: number } | null = null;
    private clipboardMarqueeAnimationFrame: number | null = null;
    private onPasteOptions: ((context: PasteOptionsContext) => void) | null = null;
    private onFillOptions: ((context: FillOptionsContext) => void) | null = null;
    private validationDropdownAvailable = false;
    private onValidationDropdownRequest: (() => void) | null = null;
    private formulaRangeSelectionHandler: GridFormulaRangeSelectionHandler | null = null;

    // Selection State
    public selection: { col: number, row: number, endCol: number, endRow: number } | null = null;
    private onSelectionChange: ((selection: { col: number, row: number, endCol: number, endRow: number } | null) => void) | null = null;

    // Layout Cache (Local)
    private colWidths: Map<number, number> = new Map();
    private rowHeights: Map<number, number> = new Map();
    private static readonly DEFAULT_COL_WIDTH = 100;
    private static readonly DEFAULT_ROW_HEIGHT = 24;

    // Data Store (Local - Phase 3)
    private cellData: Map<string, string> = new Map();
    private cellFormat: Map<string, CellFormat> = new Map();
    private displayCellFormat: Map<string, CellFormat> = new Map();
    private colFormat: Map<number, CellFormat> = new Map();
    // Data mode: in spreadsheet tabs we render from streamer/WASM; in Query Builder preview we render from local buffers.
    private dataMode: 'streamer' | 'local' = 'streamer';
    private localQueryPreviewActive = false;

    private animationFrameId: number | null = null;
    private renderPending: boolean = false;

    // Callbacks
    private onScrollChange: ((x: number, y: number) => void) | null = null;
    private onResize: ((width: number, height: number) => void) | null = null;
    private onStartEdit: ((col: number, row: number, rect: { x: number, y: number, w: number, h: number }, initialInput?: string, format?: CellFormat) => void) | null = null;
    private onRowCountChange: ((count: number) => void) | null = null;
    private onOpenHyperlinkRequest: ((context: CellHyperlinkActivationContext) => void) | null = null;

    setResizeCallback(cb: (width: number, height: number) => void) {
        this.onResize = cb;
    }

    setRowCountChangeCallback(cb: (count: number) => void) {
        this.onRowCountChange = cb;
    }

    // Dimensions
    private colCount: number = 26;
    private rowCount: number = 1000;
    private frozenRowsTop: number = 0;
    private frozenColsLeft: number = 0;
    private suppressSharedLayoutWrites: boolean = false;

    public getColCount() { return this.colCount; }
    public getRowCount() { return this.rowCount; }

    setFrozenRowsTop(count: number) {
        const next = Math.max(0, Math.floor(count || 0));
        if (this.frozenRowsTop === next) return;
        this.frozenRowsTop = next;
        this.requestRender();
    }

    getFrozenRowsTop(): number {
        return this.frozenRowsTop;
    }

    setFrozenColumnsLeft(count: number) {
        const next = Math.max(0, Math.floor(count || 0));
        if (this.frozenColsLeft === next) return;
        this.frozenColsLeft = next;
        this.requestRender();
    }

    getFrozenColumnsLeft(): number {
        return this.frozenColsLeft;
    }

    setFrozenPanes(rows: number, cols: number, opts?: { persist?: boolean; sheetId?: number | null }): { rows: number; cols: number } {
        const nextRows = Math.max(0, Math.min(this.rowCount, Math.floor(Number(rows) || 0)));
        const nextCols = Math.max(0, Math.min(this.colCount, Math.floor(Number(cols) || 0)));
        const changed = this.frozenRowsTop !== nextRows || this.frozenColsLeft !== nextCols;
        this.frozenRowsTop = nextRows;
        this.frozenColsLeft = nextCols;
        if (changed) this.requestRender();
        if (opts?.persist !== false && this.streamer) {
            this.streamer.setFrozenPanes(nextRows, nextCols, { sheetId: opts?.sheetId });
        }
        return { rows: nextRows, cols: nextCols };
    }

    freezePanesAtSelection(): { rows: number; cols: number } {
        if (this.frozenRowsTop > 0 || this.frozenColsLeft > 0) {
            return this.setFrozenPanes(0, 0);
        }
        const rows = Math.max(0, Math.min(this.rowCount, Math.floor(Number(this.selection?.row ?? 1) || 0)));
        const cols = Math.max(0, Math.min(this.colCount, Math.floor(Number(this.selection?.col ?? 1) || 0)));
        return this.setFrozenPanes(rows, cols);
    }

    toggleFreezeRowsAtSelection(): number {
        const current = this.getFrozenRowsTop();
        const next = current > 0
            ? 0
            : Math.max(1, Math.min(this.rowCount, Math.floor(Number(this.selection?.row ?? 1) || 1)));
        this.setFrozenPanes(next, 0);
        return next;
    }

    getGridlinesVisible(): boolean {
        return this.gridlinesVisible;
    }

    setGridlinesVisible(visible: boolean) {
        const next = Boolean(visible);
        if (this.gridlinesVisible === next) return;
        this.gridlinesVisible = next;
        this.requestRender();
    }

    toggleGridlinesVisible(): boolean {
        const next = !this.gridlinesVisible;
        this.setGridlinesVisible(next);
        return next;
    }

    getZoom(): number {
        return this.zoom;
    }

    setZoom(zoom: number) {
        const next = Math.max(0.5, Math.min(2, Number.isFinite(Number(zoom)) ? Number(zoom) : 1));
        if (Math.abs(this.zoom - next) < 0.001) return;
        const currentCenterX = this.scrollX + (this.viewportWidth / 2);
        const currentCenterY = this.scrollY + (this.viewportHeight / 2);
        const ratio = next / this.zoom;
        this.zoom = next;
        this.invalidateCache();
        this.setScroll(
            Math.max(0, (currentCenterX * ratio) - (this.viewportWidth / 2)),
            Math.max(0, (currentCenterY * ratio) - (this.viewportHeight / 2)),
            { renderMode: 'direct' },
        );
        this.requestRender();
    }

    setSuppressSharedLayoutWrites(suppress: boolean) {
        const next = Boolean(suppress);
        if (this.suppressSharedLayoutWrites === next) return;
        this.suppressSharedLayoutWrites = next;
    }

    constructor() { }

    private inputTarget: HTMLElement | null = null;
    private hasFocus: boolean = false;
    private hostHandlesUndoRedoShortcuts: boolean = false;

    private isEditableTarget(t: EventTarget | null): boolean {
        if (!t) return false;
        const el = t as any;
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return true;
        if (el instanceof HTMLElement) {
            try {
                return !!el.closest('input, textarea, [contenteditable="true"]');
            } catch {
                return false;
            }
        }
        return false;
    }

    private isWithinGrid(t: EventTarget | null): boolean {
        if (!t) return false;
        const node = t as any;
        if (!this.inputTarget || !node) return false;
        try {
            return this.inputTarget.contains(node as Node) || (this.canvas ? this.canvas.contains(node as Node) : false);
        } catch {
            return false;
        }
    }

    private updateFocusFromEventTarget(t: EventTarget | null) {
        const nextFocus = this.isWithinGrid(t);
        if (this.hasFocus === nextFocus && (!nextFocus || GridController.focusedController === this)) {
            return;
        }
        this.hasFocus = nextFocus;
        if (nextFocus) {
            GridController.focusedController = this;
        } else if (GridController.focusedController === this) {
            GridController.focusedController = null;
        }
    }

    private handleGlobalPointerDown = (e: PointerEvent) => {
        this.updateFocusFromEventTarget(e.target);
    }

    attach(canvas: HTMLCanvasElement, inputTarget?: HTMLElement) {
        if (this.canvas) {
            this.removeEventListeners();
        }
        this.canvas = canvas;
        this.inputTarget = inputTarget || canvas;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (ctx) {
            this.renderer = new CanvasRenderer(canvas);
            this.textRenderer = new TextRenderer(ctx);
        }

        this.setupEventListeners();

        // Initial resize
        const rect = canvas.getBoundingClientRect();
        this.resize(rect.width, rect.height);
        // Render once on attach
        this.requestRender();
    }

    dispose() {
        this.stopRenderLoop();
        this.stopClipboardMarqueeLoop();
        this.removeEventListeners();
        if (GridController.focusedController === this) {
            GridController.focusedController = null;
        }
        this.canvas = null;
        this.inputTarget = null;
        this.renderer = null;
        this.textRenderer = null;
        this.sharedState = null;
        this.streamer = null;
        this.clipboardVisual = null;
        this.onPasteOptions = null;
        this.onFillOptions = null;
        this.onOpenHyperlinkRequest = null;
        this.localQueryPreviewActive = false;
        this.colWidths.clear();
        this.rowHeights.clear();
    }

    setStreamer(streamer: ViewportStreamer) {
        this.streamer = streamer;
        this.dataMode = 'streamer';
        this.localQueryPreviewActive = false;
        this.streamer.setCallback(() => {
            this.requestRender();
        });
        this.streamer.setViewportUpdater(() => {
            this.updateStreamerViewport();
        });
        // Push initial viewport state
        this.streamer.setViewport(this.viewportWidth, this.viewportHeight);
        this.updateStreamerViewport();
    }

    detachStreamerIfCurrent(streamer: ViewportStreamer) {
        if (this.streamer === streamer) {
            this.streamer = null;
        }
    }

    // Data Methods
    setCellValue(col: number, row: number, value: string, opts?: { validationOverride?: boolean }) {
        if (this.dataMode === 'streamer' && this.streamer) {
            this.streamer.setCellValue(col, row, value, opts);
        } else {
            throw new Error('GridController.setCellValue requires a spreadsheet engine streamer; use setQueryPreview for local projection data')
        }
        this.requestRender();
    }

    private isAutoSumCandidateCell(col: number, row: number): boolean {
        if (col < 0 || row < 0 || col >= this.colCount || row >= this.rowCount) return false;
        const text = String(this.getCellValue(col, row) ?? '').trim();
        if (!text || text.startsWith('=')) return false;
        const normalized = text.replace(/[$,%\s,]/g, '');
        return normalized.length > 0 && Number.isFinite(Number(normalized));
    }

    private inferAutoSumRange(col: number, row: number): FormulaRangeCoord | null {
        if (row > 0 && this.isAutoSumCandidateCell(col, row - 1)) {
            let startRow = row - 1;
            while (startRow > 0 && this.isAutoSumCandidateCell(col, startRow - 1)) {
                startRow -= 1;
            }
            return {
                start: { col, row: startRow },
                end: { col, row: row - 1 },
            };
        }
        if (col > 0 && this.isAutoSumCandidateCell(col - 1, row)) {
            let startCol = col - 1;
            while (startCol > 0 && this.isAutoSumCandidateCell(startCol - 1, row)) {
                startCol -= 1;
            }
            return {
                start: { col: startCol, row },
                end: { col: col - 1, row },
            };
        }
        return null;
    }

    private startAutoSumFormulaEdit(col: number, row: number): boolean {
        const range = this.inferAutoSumRange(col, row);
        const draft = range ? `=SUM(${formatFormulaRangeRef(range)})` : '=SUM(';
        this.startEditing(col, row, draft);
        return true;
    }

    startAutoSumAtSelection(): boolean {
        if (!this.selection) return false;
        return this.startAutoSumFormulaEdit(this.selection.col, this.selection.row);
    }

    private selectedEngineRange(): { startCol: number; startRow: number; endCol: number; endRow: number } | null {
        if (!this.selection) return null;
        const selection = this.normalizeSelection(this.selection);
        const maxCol = Math.max(0, this.colCount - 1);
        const maxRow = Math.max(0, this.rowCount - 1);
        const startCol = Math.max(0, Math.min(maxCol, selection.col));
        const startRow = Math.max(0, Math.min(maxRow, selection.row));
        const endCol = Math.max(startCol, Math.min(maxCol, selection.endCol));
        const endRow = Math.max(startRow, Math.min(maxRow, selection.endRow));
        return { startCol, startRow, endCol, endRow };
    }

    applyAutoFilterToSelection(): boolean {
        const range = this.selectedEngineRange();
        if (!range) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.applyAutoFilterToSelection requires a spreadsheet engine streamer')
        }
        this.streamer.setAutoFilter(range, []);
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    private executeTableMutationPlan(plan: TableMutationPlan, errorContext: string): boolean {
        if (plan.kind === 'noop') return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error(`GridController.${errorContext} requires a spreadsheet engine streamer`)
        }
        switch (plan.kind) {
            case 'upsert-table':
                this.streamer.upsertTable(plan.range, plan.options);
                break;
            case 'delete-table':
                this.streamer.deleteTable(plan.tableId);
                break;
            case 'set-total-row': {
                const operations = this.streamer.setTableTotalRow(plan.table, plan.showTotals);
                if (operations.length <= 0) return false;
                break;
            }
        }
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    private executeDerivedArtifactMutationPlan(plan: DerivedArtifactMutationPlan, errorContext: string): boolean {
        if (plan.kind === 'noop') return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error(`GridController.${errorContext} requires a spreadsheet engine streamer`)
        }
        switch (plan.kind) {
            case 'upsert-derived-artifact':
                this.streamer.upsertDerivedArtifact(plan.artifactKind, plan.range, plan.options);
                break;
            case 'update-derived-artifact':
                this.streamer.updateDerivedArtifact(plan.artifact, plan.options);
                this.derivedArtifactProjection = {
                    ...this.derivedArtifactProjection,
                    [plan.artifact.id]: cloneDerivedArtifact(plan.artifact),
                };
                break;
            case 'delete-derived-artifact':
                this.streamer.deleteDerivedArtifact(plan.artifactId);
                if (Object.prototype.hasOwnProperty.call(this.derivedArtifactProjection, plan.artifactId)) {
                    const remainingArtifacts = { ...this.derivedArtifactProjection };
                    delete remainingArtifacts[plan.artifactId];
                    this.derivedArtifactProjection = remainingArtifacts;
                }
                break;
        }
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    createTableFromSelection(styleId?: string): boolean {
        const range = this.selectedEngineRange();
        return this.executeTableMutationPlan(buildCreateTableFromSelectionPlan(range, styleId), 'createTableFromSelection');
    }

    formatSelectionAsTable(styleId: string = 'TableStyleMedium2'): boolean {
        const normalizedStyleId = String(styleId ?? '').trim() || 'TableStyleMedium2';
        if (this.selection) {
            const table = this.getTableAtCell(this.selection.col, this.selection.row);
            if (table) {
                return this.executeTableMutationPlan(buildSetTableStylePlan(table, normalizedStyleId), 'formatSelectionAsTable');
            }
        }
        const range = this.selectedEngineRange();
        return this.executeTableMutationPlan(buildCreateTableFromSelectionPlan(range, normalizedStyleId), 'formatSelectionAsTable');
    }

    insertChartArtifactFromSelection(chartType: SpreadsheetDerivedArtifact['chart_type'] = 'column'): boolean {
        const range = this.selectedEngineRange();
        return this.executeDerivedArtifactMutationPlan(
            buildExcelRangeChartInsertPlan({ range, chartType }),
            'insertChartArtifactFromSelection',
        );
    }

    insertChartArtifactFromRange(range: SpreadsheetRangeAddress, chartType: SpreadsheetChartArtifactType = 'column'): boolean {
        return this.executeDerivedArtifactMutationPlan(
            buildExcelRangeChartInsertPlan({
                range: {
                    startCol: range.start_col,
                    startRow: range.start_row,
                    endCol: range.end_col,
                    endRow: range.end_row,
                },
                chartType,
            }),
            'insertChartArtifactFromRange',
        );
    }

    insertPivotArtifactFromSelection(): boolean {
        const range = this.selectedEngineRange();
        return this.executeDerivedArtifactMutationPlan(
            buildDerivedArtifactInsertPlan({ artifactKind: 'pivot', range }),
            'insertPivotArtifactFromSelection',
        );
    }

    getDerivedArtifactAtCell(col: number, row: number): SpreadsheetDerivedArtifact | null {
        const artifacts = Object.values(this.derivedArtifactProjection)
            .filter((artifact) => artifact?.placement)
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
        for (const artifact of artifacts) {
            const range = artifact.placement;
            if (
                col >= range.start_col &&
                col <= range.end_col &&
                row >= range.start_row &&
                row <= range.end_row
            ) {
                return cloneDerivedArtifact(artifact);
            }
        }
        return null;
    }

    getDerivedArtifactById(artifactId: string): SpreadsheetDerivedArtifact | null {
        const artifact = this.derivedArtifactProjection[String(artifactId ?? '')];
        return artifact ? cloneDerivedArtifact(artifact) : null;
    }

    selectDerivedArtifactAtCell(col: number, row: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        if (!artifact) return false;
        const range = artifact.placement;
        const selected = this.setSelection({
            col: range.start_col,
            row: range.start_row,
            endCol: range.end_col,
            endRow: range.end_row,
        }, { force: true });
        if (!selected) return false;
        this.requestRender();
        return true;
    }

    selectDerivedArtifactSourceRangeById(artifactId: string): boolean {
        const artifact = this.getDerivedArtifactById(artifactId);
        if (!artifact?.source_range) return false;
        const range = artifact.source_range;
        if (range.sheet_id !== artifact.sheet_id) return false;
        const selected = this.setSelection({
            col: range.start_col,
            row: range.start_row,
            endCol: range.end_col,
            endRow: range.end_row,
        }, { force: true });
        if (!selected) return false;
        this.requestRender();
        return true;
    }

    selectDerivedArtifactSourceRangeAtCell(col: number, row: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return artifact ? this.selectDerivedArtifactSourceRangeById(artifact.id) : false;
    }

    renameDerivedArtifactAtCell(col: number, row: number, nextTitle: string): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildRenameDerivedArtifactPlan({ artifact, nextTitle }),
            'renameDerivedArtifactAtCell',
        );
    }

    resizeDerivedArtifactAtCellToSelection(col: number, row: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        const range = this.selectedEngineRange();
        return this.executeDerivedArtifactMutationPlan(
            buildResizeDerivedArtifactToSelectionPlan({ artifact, range }),
            'resizeDerivedArtifactAtCellToSelection',
        );
    }

    resizeDerivedArtifactAtCellToCell(col: number, row: number, targetCol: number, targetRow: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildResizeDerivedArtifactToCellPlan({
                artifact,
                targetCol,
                targetRow,
                colCount: this.colCount,
                rowCount: this.rowCount,
            }),
            'resizeDerivedArtifactAtCellToCell',
        );
    }

    moveDerivedArtifactAtCellToCell(col: number, row: number, targetCol: number, targetRow: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildMoveDerivedArtifactToCellPlan({
                artifact,
                targetCol,
                targetRow,
                colCount: this.colCount,
                rowCount: this.rowCount,
            }),
            'moveDerivedArtifactAtCellToCell',
        );
    }

    setDerivedArtifactSourceRangeAtCellToSelection(col: number, row: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        const range = this.selectedEngineRange();
        return this.executeDerivedArtifactMutationPlan(
            buildSetDerivedArtifactSourceToSelectionPlan({ artifact, range }),
            'setDerivedArtifactSourceRangeAtCellToSelection',
        );
    }

    setDerivedArtifactSourceRangeAtCellFromDraft(col: number, row: number, sourceRangeDraft: string): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildSetDerivedArtifactSourceRangeDraftPlan({
                artifact,
                sourceRangeDraft,
                colCount: this.colCount,
                rowCount: this.rowCount,
            }),
            'setDerivedArtifactSourceRangeAtCellFromDraft',
        );
    }

    resizeDerivedArtifactSourceRangeByIdToCell(artifactId: string, targetCol: number, targetRow: number): boolean {
        const artifact = this.getDerivedArtifactById(artifactId);
        return this.executeDerivedArtifactMutationPlan(
            buildResizeDerivedArtifactSourceToCellPlan({
                artifact,
                targetCol,
                targetRow,
                colCount: this.colCount,
                rowCount: this.rowCount,
            }),
            'resizeDerivedArtifactSourceRangeByIdToCell',
        );
    }

    moveDerivedArtifactSourceRangeByIdToCell(artifactId: string, targetCol: number, targetRow: number): boolean {
        const artifact = this.getDerivedArtifactById(artifactId);
        return this.executeDerivedArtifactMutationPlan(
            buildMoveDerivedArtifactSourceToCellPlan({
                artifact,
                targetCol,
                targetRow,
                colCount: this.colCount,
                rowCount: this.rowCount,
            }),
            'moveDerivedArtifactSourceRangeByIdToCell',
        );
    }

    setChartArtifactTypeAtCell(col: number, row: number, chartType: SpreadsheetChartArtifactType): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildSetDerivedArtifactChartTypePlan({ artifact, chartType }),
            'setChartArtifactTypeAtCell',
        );
    }

    setChartArtifactMetadataAtCell(col: number, row: number, update: ExcelRangeChartMetadataUpdate): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildSetDerivedArtifactChartMetadataPlan({ artifact, update }),
            'setChartArtifactMetadataAtCell',
        );
    }

    deleteDerivedArtifactAtCell(col: number, row: number): boolean {
        const artifact = this.getDerivedArtifactAtCell(col, row);
        return this.executeDerivedArtifactMutationPlan(
            buildDeleteDerivedArtifactPlan(artifact),
            'deleteDerivedArtifactAtCell',
        );
    }

    getTableAtCell(col: number, row: number): SpreadsheetTableDefinition | null {
        const tables = Object.values(this.tableProjection)
            .filter((table) => table?.range)
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
        for (const table of tables) {
            const range = table.range;
            if (
                col >= range.start_col &&
                col <= range.end_col &&
                row >= range.start_row &&
                row <= range.end_row
            ) {
                return cloneTableDefinition(table);
            }
        }
        return null;
    }

    private getTableEdgeHitAtPoint(x: number, y: number): SpreadsheetTableDefinition | null {
        const edgeSlop = 6;
        const tables = Object.values(this.tableProjection)
            .filter((table) => table?.range)
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
        for (const table of tables) {
            const range = table.range;
            const rect = this.getRangeScreenRect({
                start: { col: range.start_col, row: range.start_row },
                end: { col: range.end_col, row: range.end_row },
            });
            if (!rect || rect.w <= 0 || rect.h <= 0) continue;
            const left = rect.x;
            const top = rect.y;
            const right = rect.x + rect.w;
            const bottom = rect.y + rect.h;
            const withinHorizontal = x >= left - edgeSlop && x <= right + edgeSlop;
            const withinVertical = y >= top - edgeSlop && y <= bottom + edgeSlop;
            const onEdge = (
                (Math.abs(x - left) <= edgeSlop && y >= top - edgeSlop && y <= bottom + edgeSlop) ||
                (Math.abs(x - right) <= edgeSlop && y >= top - edgeSlop && y <= bottom + edgeSlop) ||
                (Math.abs(y - top) <= edgeSlop && x >= left - edgeSlop && x <= right + edgeSlop) ||
                (Math.abs(y - bottom) <= edgeSlop && x >= left - edgeSlop && x <= right + edgeSlop)
            );
            if (withinHorizontal && withinVertical && onEdge) {
                return cloneTableDefinition(table);
            }
        }
        return null;
    }

    private selectTable(table: SpreadsheetTableDefinition): boolean {
        const range = table.range;
        const selected = this.setSelection({
            col: range.start_col,
            row: range.start_row,
            endCol: range.end_col,
            endRow: range.end_row,
        });
        if (!selected) return false;
        this.notifySelectionChange();
        this.requestRender();
        return true;
    }

    deleteTableAtCell(col: number, row: number): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildDeleteTablePlan(table), 'deleteTableAtCell');
    }

    resizeTableAtCellToSelection(col: number, row: number): boolean {
        const table = this.getTableAtCell(col, row);
        const range = this.selectedEngineRange();
        return this.executeTableMutationPlan(buildResizeTableToSelectionPlan(table, range), 'resizeTableAtCellToSelection');
    }

    toggleTableHeaderRowAtCell(col: number, row: number): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildToggleTableHeaderRowPlan(table), 'toggleTableHeaderRowAtCell');
    }

    toggleTableFilterButtonsAtCell(col: number, row: number): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildToggleTableFilterButtonsPlan(table), 'toggleTableFilterButtonsAtCell');
    }

    renameTableAtCell(col: number, row: number, nextName: string): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildRenameTablePlan(table, nextName), 'renameTableAtCell');
    }

    setTableStyleAtCell(col: number, row: number, styleId: string): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildSetTableStylePlan(table, styleId), 'setTableStyleAtCell');
    }

    toggleTableTotalRowAtCell(col: number, row: number): boolean {
        const table = this.getTableAtCell(col, row);
        return this.executeTableMutationPlan(buildToggleTableTotalRowPlan(table), 'toggleTableTotalRowAtCell');
    }

    clearAutoFilter(): boolean {
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.clearAutoFilter requires a spreadsheet engine streamer')
        }
        this.streamer.setAutoFilter(null);
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    sortSelection(descending = false): boolean {
        const range = this.selectedEngineRange();
        if (!range) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.sortSelection requires a spreadsheet engine streamer')
        }
        this.streamer.sortRange(range, {
            sortColumns: [{ col: range.startCol, descending }],
            hasHeader: false,
        });
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    removeDuplicatesFromSelection(): boolean {
        const range = this.selectedEngineRange();
        if (!range) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.removeDuplicatesFromSelection requires a spreadsheet engine streamer')
        }
        const keyColumns: number[] = [];
        for (let col = range.startCol; col <= range.endCol; col += 1) {
            keyColumns.push(col);
        }
        this.streamer.removeDuplicates(range, {
            keyColumns,
            hasHeader: false,
        });
        this.updateStreamerViewport();
        this.requestRender();
        return true;
    }

    clearSelectedCells() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const startRow = Math.min(row, endRow);
        const finalCol = Math.max(col, endCol);
        const finalRow = Math.max(row, endRow);
        const width = finalCol - startCol + 1;
        const height = finalRow - startRow + 1;

        if (this.dataMode === 'streamer' && this.streamer) {
            this.streamer.clearRange(startCol, startRow, width, height);
        } else {
            if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
                throw new Error('GridController.clearSelectedCells requires a spreadsheet engine streamer or local query preview mode')
            }
            for (let r = startRow; r <= finalRow; r++) {
                for (let c = startCol; c <= finalCol; c++) {
                    this.cellData.delete(`${c},${r}`);
                }
            }
        }
        this.requestRender();
    }

    /**
     * Replace the local (non-streamer) cell buffer with a query preview (header + rows).
     * Used by Query Builder's Grid Preview. Must not write to WASM/streamer.
     */
    setQueryPreview(headers: string[], rows: any[][]) {
        // Force local buffer mode for preview rendering (avoid streamer/WASM sheet writes).
        this.dataMode = 'local';
        this.localQueryPreviewActive = true;
        this.cellData.clear();
        this.cellFormat.clear();
        this.displayCellFormat.clear();
        this.colFormat.clear();

        const cols = Math.max(0, headers?.length ?? 0);
        const dataRows = Array.isArray(rows) ? rows : [];

        // Header row (row 0)
        for (let c = 0; c < cols; c++) {
            const h = String(headers[c] ?? `col_${c}`);
            this.cellData.set(`${c},0`, h);
            this.cellFormat.set(`${c},0`, {
                bold: true,
                backgroundColor: '#f6f1ea',
                textColor: '#3a3228',
            });
        }

        // Data rows start at row 1
        const limit = Math.min(dataRows.length, 5000);
        for (let r = 0; r < limit; r++) {
            const row = Array.isArray(dataRows[r]) ? dataRows[r] : [];
            for (let c = 0; c < cols; c++) {
                const v = row[c];
                const s = (v === null || v === undefined) ? '' : String(v);
                if (s) this.cellData.set(`${c},${r + 1}`, s);
            }
        }

        this.requestRender();
    }

    setLocalPreviewCellValue(col: number, row: number, value: string) {
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.setLocalPreviewCellValue requires local query preview mode')
        }
        const key = `${col},${row}`;
        const next = String(value ?? '');
        if (next) this.cellData.set(key, next);
        else this.cellData.delete(key);
        this.requestRender();
    }

    getCellValue(col: number, row: number): string | undefined {
        if (this.dataMode === 'streamer' && this.streamer) {
            return this.streamer.getCellValue(col, row);
        }
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) return undefined;
        return this.cellData.get(`${col},${row}`);
    }

    /**
     * Export the current local grid buffer to CSV.
     * Intended for Query Builder preview/parity capture (NOT streamer/WASM sheets).
     */
    exportLocalCsv(opts?: { maxRows?: number; maxCols?: number }): string {
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('exportLocalCsv is only supported for local (preview) grids')
        }
        const maxRows = Math.max(1, Math.floor(Number(opts?.maxRows ?? this.rowCount)))
        const maxCols = Math.max(1, Math.floor(Number(opts?.maxCols ?? this.colCount)))
        const rows = Math.min(this.rowCount, maxRows)
        const cols = Math.min(this.colCount, maxCols)
        const esc = (v: any) => {
            const s = String(v ?? '')
            if (/[",\n\r]/.test(s)) return `"${s.replaceAll('"', '""')}"`
            return s
        }
        const lines: string[] = []
        for (let r = 0; r < rows; r++) {
            const parts: string[] = []
            for (let c = 0; c < cols; c++) {
                const v = this.getCellValue(c, r) ?? ''
                parts.push(esc(v))
            }
            lines.push(parts.join(','))
        }
        return lines.join('\n')
    }

    getCellFormula(col: number, row: number): string | undefined {
        if (this.dataMode === 'streamer' && this.streamer) {
            return this.streamer.getCellFormula(col, row);
        }
        // Local fallback (Phase 3 style, though we didn't have formulas then)
        return undefined;
    }

    setCellFormat(col: number, row: number, format: CellFormat) {
        if (this.dataMode === 'streamer' && this.streamer) {
            this.streamer.setCellFormat(col, row, format);
        } else {
            throw new Error('GridController.setCellFormat requires a spreadsheet engine streamer; use setQueryPreview for local projection data')
        }
        this.requestRender();
    }

    setCommentOnSelection(comment: string | null): boolean {
        if (!this.selection) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.setCommentOnSelection requires a spreadsheet engine streamer')
        }
        const selection = this.normalizeSelection(this.selection);
        this.streamer.setCellComment(selection.col, selection.row, comment);
        this.requestRender();
        return true;
    }

    setHyperlinkOnSelection(hyperlink: string | null): boolean {
        if (!this.selection) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.setHyperlinkOnSelection requires a spreadsheet engine streamer')
        }
        const selection = this.normalizeSelection(this.selection);
        this.streamer.setCellHyperlink(selection.col, selection.row, hyperlink);
        this.requestRender();
        return true;
    }

    setValidationOnSelection(validationId: string | null): boolean {
        if (!this.selection) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.setValidationOnSelection requires a spreadsheet engine streamer')
        }
        const selection = this.normalizeSelection(this.selection);
        this.streamer.setRangeValidation(
            selection.col,
            selection.row,
            selection.endCol,
            selection.endRow,
            validationId,
        );
        this.requestRender();
        return true;
    }

    setValidationRuleOnSelection(rule: Omit<SpreadsheetValidationRule, 'id'> & { id?: string | null }): boolean {
        if (!this.selection) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.setValidationRuleOnSelection requires a spreadsheet engine streamer')
        }
        const selection = this.normalizeSelection(this.selection);
        this.streamer.setRangeValidationRule(
            selection.col,
            selection.row,
            selection.endCol,
            selection.endRow,
            rule,
        );
        this.requestRender();
        return true;
    }

    setProtectionOnSelection(protection: Record<string, unknown> | null): boolean {
        if (!this.selection) return false;
        if (this.dataMode !== 'streamer' || !this.streamer) {
            throw new Error('GridController.setProtectionOnSelection requires a spreadsheet engine streamer')
        }
        const selection = this.normalizeSelection(this.selection);
        this.streamer.setRangeProtection(
            selection.col,
            selection.row,
            selection.endCol,
            selection.endRow,
            protection,
        );
        this.requestRender();
        return true;
    }

    setLocalPreviewCellFormat(col: number, row: number, format: CellFormat) {
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.setLocalPreviewCellFormat requires local query preview mode')
        }
        const key = `${col},${row}`;
        this.cellFormat.set(key, format);
        this.requestRender();
    }

    setDisplayCellFormats(startCol: number, startRow: number, matrix: Array<Array<CellFormat | null>>) {
        for (let r = 0; r < matrix.length; r++) {
            const row = Array.isArray(matrix[r]) ? matrix[r] : [];
            for (let c = 0; c < row.length; c++) {
                const format = row[c];
                const key = `${startCol + c},${startRow + r}`;
                if (format && Object.keys(format).length > 0) this.displayCellFormat.set(key, format);
                else this.displayCellFormat.delete(key);
            }
        }
        this.requestRender();
    }

    clearDisplayCellFormats(startCol: number, startRow: number, rows: number, cols: number) {
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                this.displayCellFormat.delete(`${startCol + c},${startRow + r}`);
            }
        }
        this.requestRender();
    }

    clearAllDisplayCellFormats() {
        if (this.displayCellFormat.size <= 0) return;
        this.displayCellFormat.clear();
        this.requestRender();
    }

    getCellFormat(col: number, row: number): CellFormat | undefined {
        const key = `${col},${row}`;
        const display = this.displayCellFormat.get(key);
        if (this.dataMode === 'streamer' && this.streamer) {
            const base = this.streamer.getCellFormat(col, row);
            const table = this.getTableCellFormatOverlay(col, row);
            const withTable = table ? mergeCellFormat(base, table) : base;
            if (display && withTable) return mergeCellFormat(withTable, display);
            return display || withTable;
        }
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) return display;
        const cell = this.cellFormat.get(key);
        if (display && cell) return mergeCellFormat(cell, display);
        if (display) return display;
        if (cell) return cell;
        // Column-level defaults (Query Builder preview)
        return this.colFormat.get(col);
    }

    getCellMetadata(col: number, row: number): CellMetadataProjection | undefined {
        if (this.dataMode === 'streamer' && this.streamer) {
            return this.streamer.getCellMetadata(col, row);
        }
        return undefined;
    }

    setSheetProtectionProjection(protection: SheetProtectionProjection) {
        const enabled = protection?.enabled === true;
        const next: SheetProtectionProjection = enabled ? {
            enabled: true,
            allow_select_locked_cells: protection?.allow_select_locked_cells !== false,
            allow_select_unlocked_cells: protection?.allow_select_unlocked_cells !== false,
            allow_format_cells: protection?.allow_format_cells === true,
            allow_resize_rows: protection?.allow_resize_rows === true,
            allow_resize_columns: protection?.allow_resize_columns === true,
            allow_insert_rows: protection?.allow_insert_rows === true,
            allow_insert_columns: protection?.allow_insert_columns === true,
            allow_delete_rows: protection?.allow_delete_rows === true,
            allow_delete_columns: protection?.allow_delete_columns === true,
        } : null;
        if (JSON.stringify(this.sheetProtectionProjection ?? null) === JSON.stringify(next)) return;
        this.sheetProtectionProjection = next;
        this.requestRender();
    }

    getSheetProtectionProjection(): SheetProtectionProjection {
        return this.sheetProtectionProjection ? { ...this.sheetProtectionProjection } : null;
    }

    setAutoFilterProjection(autoFilter: SpreadsheetAutoFilter | null) {
        const next = autoFilter ? { ...autoFilter } : null;
        if (JSON.stringify(this.autoFilterProjection ?? null) === JSON.stringify(next)) return;
        this.autoFilterProjection = next;
        this.requestRender();
    }

    getAutoFilterProjection(): SpreadsheetAutoFilter | null {
        return this.autoFilterProjection ? { ...this.autoFilterProjection } : null;
    }

    setTableProjection(tables: Record<string, SpreadsheetTableDefinition> | null | undefined) {
        const next = tables ? { ...tables } : {};
        if (JSON.stringify(this.tableProjection) === JSON.stringify(next)) return;
        this.tableProjection = next;
        this.requestRender();
    }

    getTableProjection(): Record<string, SpreadsheetTableDefinition> {
        return { ...this.tableProjection };
    }

    setDerivedArtifactProjection(artifacts: Record<string, SpreadsheetDerivedArtifact> | null | undefined) {
        const next = artifacts ? { ...artifacts } : {};
        if (JSON.stringify(this.derivedArtifactProjection) === JSON.stringify(next)) return;
        this.derivedArtifactProjection = next;
        this.requestRender();
    }

    getDerivedArtifactProjection(): Record<string, SpreadsheetDerivedArtifact> {
        return { ...this.derivedArtifactProjection };
    }

    private getTableCellFormatOverlay(col: number, row: number): CellFormat | undefined {
        const table = this.getTableAtCell(col, row);
        if (!table) return undefined;
        const range = table.range;
        const hasHeaders = table.has_headers !== false;
        const style = tableStyleVisualFor(table.style_id);
        if (hasHeaders && row === range.start_row) return style.header;
        const hasTotals = table.show_totals === true;
        if (hasTotals && row === range.end_row) return style.total;
        const firstBodyRow = hasHeaders ? range.start_row + 1 : range.start_row;
        const lastBodyRow = hasTotals ? range.end_row - 1 : range.end_row;
        if (row > lastBodyRow) return undefined;
        const bodyOffset = row - firstBodyRow;
        if (bodyOffset < 0) return undefined;
        return bodyOffset % 2 === 1 && style.banded
            ? mergeCellFormat(style.body, style.banded)
            : style.body;
    }

    getCellProtectionVisualState(col: number, row: number): CellProtectionVisualState {
        const protection = this.getCellMetadata(col, row)?.protection;
        if (protection) {
            return Object.prototype.hasOwnProperty.call(protection, 'locked') && protection.locked === false
                ? 'explicit-unlocked'
                : 'explicit-locked';
        }
        return this.sheetProtectionProjection?.enabled === true ? 'sheet-locked' : null;
    }

    isCellSelectableBySheetProtection(col: number, row: number): boolean {
        if (this.sheetProtectionProjection?.enabled !== true) return true;
        const protection = this.getCellMetadata(col, row)?.protection;
        const explicitUnlocked = Boolean(
            protection &&
            Object.prototype.hasOwnProperty.call(protection, 'locked') &&
            protection.locked === false,
        );
        return explicitUnlocked
            ? this.sheetProtectionProjection.allow_select_unlocked_cells !== false
            : this.sheetProtectionProjection.allow_select_locked_cells !== false;
    }

    private isSelectionSelectableBySheetProtection(selection: GridSelectionRange): boolean {
        const normalized = this.normalizeSelection(selection);
        const cells = [
            { col: selection.col, row: selection.row },
            { col: selection.endCol, row: selection.endRow },
            { col: normalized.col, row: normalized.row },
            { col: normalized.endCol, row: normalized.endRow },
        ];
        return cells.every((cell) => this.isCellSelectableBySheetProtection(cell.col, cell.row));
    }

    private canFormatSelectionBySheetProtection(): boolean {
        return this.sheetProtectionProjection?.enabled !== true ||
            this.sheetProtectionProjection.allow_format_cells === true;
    }

    getAuthoringCellFormat(col: number, row: number): CellFormat | undefined {
        const key = `${col},${row}`;
        if (this.dataMode === 'streamer' && this.streamer) {
            return this.streamer.getCellFormat(col, row);
        }
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) return undefined;
        const cell = this.cellFormat.get(key);
        if (cell) return cell;
        return this.colFormat.get(col);
    }

    /**
     * Apply a column-wide format in local preview mode (Query Builder).
     * Header row can be independently styled via setCellFormat(c, 0, ...).
     */
    setColumnFormat(col: number, format: CellFormat | null) {
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.setColumnFormat requires local query preview mode')
        }
        if (format) this.colFormat.set(col, format);
        else this.colFormat.delete(col);
        this.requestRender();
    }

    setDataMode(mode: 'streamer' | 'local') {
        const next = mode === 'local' ? 'local' : 'streamer';
        if (this.dataMode === next) return;
        this.dataMode = next;
        if (next === 'streamer') this.localQueryPreviewActive = false;
        this.requestRender();
    }

    getDataMode(): 'streamer' | 'local' {
        return this.dataMode;
    }

    hasLocalQueryPreview(): boolean {
        return this.dataMode === 'local' && this.localQueryPreviewActive;
    }

    findTextInLocalPreview(query: string): SpreadsheetFindMatch[] {
        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) return [];
        const maxRows = Math.max(0, Math.floor(Number(this.rowCount) || 0));
        const maxCols = Math.max(0, Math.floor(Number(this.colCount) || 0));
        const matches: SpreadsheetFindMatch[] = [];
        for (let row = 0; row < maxRows; row += 1) {
            for (let col = 0; col < maxCols; col += 1) {
                const value = String(this.getCellValue(col, row) ?? '');
                if (cellTextMatchesQuery(value, query)) {
                    matches.push({ col, row, data: { value } });
                }
            }
        }
        return matches;
    }

    applyFormatToSelection(format: Partial<CellFormat>) {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);

        if (this.dataMode === 'streamer' && this.streamer) {
            const current = this.getAuthoringCellFormat(col, row) || {};
            const rowCount = Math.max(0, Math.floor(Number(this.rowCount) || 0));
            const colCount = Math.max(0, Math.floor(Number(this.colCount) || 0));
            const coversFullRows = colCount > 0 && startCol === 0 && maxCol >= colCount - 1;
            const coversFullCols = rowCount > 0 && startRow === 0 && maxRow >= rowCount - 1;
            const streamer = this.streamer as ViewportStreamer & {
                getRowFormat?: (rowIndex: number) => CellFormat | null;
                getColumnFormat?: (colIndex: number) => CellFormat | null;
                setRowFormat?: (rowIndex: number, count: number, format: CellFormat | null) => void;
                setColumnFormat?: (colIndex: number, count: number, format: CellFormat | null) => void;
            };
            if (coversFullRows && typeof streamer.setRowFormat === 'function') {
                if (typeof streamer.getRowFormat === 'function') {
                    let runStart = startRow;
                    let runFormat = mergeCellFormat(streamer.getRowFormat(startRow) || {}, format);
                    let runKey = JSON.stringify(runFormat);
                    for (let rowIndex = startRow + 1; rowIndex <= maxRow; rowIndex += 1) {
                        const nextFormat = mergeCellFormat(streamer.getRowFormat(rowIndex) || {}, format);
                        const nextKey = JSON.stringify(nextFormat);
                        if (nextKey === runKey) continue;
                        streamer.setRowFormat(runStart, rowIndex - runStart, runFormat);
                        runStart = rowIndex;
                        runFormat = nextFormat;
                        runKey = nextKey;
                    }
                    streamer.setRowFormat(runStart, maxRow - runStart + 1, runFormat);
                } else {
                    streamer.setRowFormat(startRow, maxRow - startRow + 1, mergeCellFormat(current, format));
                }
                this.requestRender();
                return;
            }
            if (coversFullCols && typeof streamer.setColumnFormat === 'function') {
                if (typeof streamer.getColumnFormat === 'function') {
                    let runStart = startCol;
                    let runFormat = mergeCellFormat(streamer.getColumnFormat(startCol) || {}, format);
                    let runKey = JSON.stringify(runFormat);
                    for (let colIndex = startCol + 1; colIndex <= maxCol; colIndex += 1) {
                        const nextFormat = mergeCellFormat(streamer.getColumnFormat(colIndex) || {}, format);
                        const nextKey = JSON.stringify(nextFormat);
                        if (nextKey === runKey) continue;
                        streamer.setColumnFormat(runStart, colIndex - runStart, runFormat);
                        runStart = colIndex;
                        runFormat = nextFormat;
                        runKey = nextKey;
                    }
                    streamer.setColumnFormat(runStart, maxCol - runStart + 1, runFormat);
                } else {
                    streamer.setColumnFormat(startCol, maxCol - startCol + 1, mergeCellFormat(current, format));
                }
                this.requestRender();
                return;
            }
            this.streamer.setRangeFormat(startCol, startRow, maxCol, maxRow, mergeCellFormat(current, format));
            this.requestRender();
            return;
        }

        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.applyFormatToSelection requires a spreadsheet engine streamer or local query preview mode')
        }

        for (let c = startCol; c <= maxCol; c++) {
            for (let r = startRow; r <= maxRow; r++) {
                const current = this.getCellFormat(c, r) || {};
                const nextFormat = mergeCellFormat(current, format);
                this.setLocalPreviewCellFormat(c, r, nextFormat);
            }
        }
        this.requestRender();
    }

    applyStaticTableStyleToSelection(style: { header: CellFormat; body: CellFormat; banded?: CellFormat }) {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);

        if (this.dataMode === 'streamer' && this.streamer) {
            this.streamer.setRangeFormat(startCol, startRow, maxCol, maxRow, style.body);
            this.streamer.setRangeFormat(startCol, startRow, maxCol, startRow, style.header);
            if (style.banded && maxRow > startRow) {
                for (let rowIndex = startRow + 1; rowIndex <= maxRow; rowIndex += 2) {
                    this.streamer.setRangeFormat(startCol, rowIndex, maxCol, rowIndex, style.banded);
                }
            }
            this.requestRender();
            return;
        }

        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.applyStaticTableStyleToSelection requires a spreadsheet engine streamer or local query preview mode')
        }

        for (let rowIndex = startRow; rowIndex <= maxRow; rowIndex++) {
            const rowFormat = rowIndex === startRow
                ? style.header
                : (style.banded && (rowIndex - startRow) % 2 === 1 ? style.banded : style.body);
            for (let colIndex = startCol; colIndex <= maxCol; colIndex++) {
                const current = this.getCellFormat(colIndex, rowIndex) || {};
                this.setLocalPreviewCellFormat(colIndex, rowIndex, { ...current, ...rowFormat });
            }
        }
        this.requestRender();
    }

    clearFormatsFromSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);

        if (this.dataMode === 'streamer' && this.streamer) {
            const rowCount = Math.max(0, Math.floor(Number(this.rowCount) || 0));
            const colCount = Math.max(0, Math.floor(Number(this.colCount) || 0));
            const coversFullRows = colCount > 0 && startCol === 0 && maxCol >= colCount - 1;
            const coversFullCols = rowCount > 0 && startRow === 0 && maxRow >= rowCount - 1;
            const streamer = this.streamer as ViewportStreamer & {
                clearRangeFormats?: (startCol: number, startRow: number, endCol: number, endRow: number) => void;
                setRowFormat?: (rowIndex: number, count: number, format: CellFormat | null) => void;
                setColumnFormat?: (colIndex: number, count: number, format: CellFormat | null) => void;
            };
            if (coversFullRows && typeof streamer.setRowFormat === 'function') {
                streamer.setRowFormat(startRow, maxRow - startRow + 1, null);
                this.requestRender();
                return;
            }
            if (coversFullCols && typeof streamer.setColumnFormat === 'function') {
                streamer.setColumnFormat(startCol, maxCol - startCol + 1, null);
                this.requestRender();
                return;
            }
            if (typeof streamer.clearRangeFormats === 'function') {
                streamer.clearRangeFormats(startCol, startRow, maxCol, maxRow);
            } else {
                streamer.setRangeFormat(startCol, startRow, maxCol, maxRow, {});
            }
            this.requestRender();
            return;
        }

        if (this.dataMode !== 'local' || !this.localQueryPreviewActive) {
            throw new Error('GridController.clearFormatsFromSelection requires a spreadsheet engine streamer or local query preview mode')
        }

        for (let c = startCol; c <= maxCol; c++) {
            for (let r = startRow; r <= maxRow; r++) {
                this.setLocalPreviewCellFormat(c, r, {});
            }
        }
        this.requestRender();
    }

    mergeSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).mergeRange !== 'function') {
            throw new Error('GridController.mergeSelection requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { mergeRange: (startCol: number, startRow: number, endCol: number, endRow: number) => void })
            .mergeRange(startCol, startRow, maxCol, maxRow);
        this.requestRender();
    }

    mergeAcrossSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).mergeAcrossRange !== 'function') {
            throw new Error('GridController.mergeAcrossSelection requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { mergeAcrossRange: (startCol: number, startRow: number, endCol: number, endRow: number) => void })
            .mergeAcrossRange(startCol, startRow, maxCol, maxRow);
        this.requestRender();
    }

    unmergeSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).unmergeRange !== 'function') {
            throw new Error('GridController.unmergeSelection requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { unmergeRange: (startCol: number, startRow: number, endCol: number, endRow: number) => void })
            .unmergeRange(startCol, startRow, maxCol, maxRow);
        this.requestRender();
    }

    upsertConditionalFormatRule(rule: SpreadsheetConditionalFormatRule) {
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).upsertConditionalFormatRule !== 'function') {
            throw new Error('GridController.upsertConditionalFormatRule requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { upsertConditionalFormatRule: (rule: SpreadsheetConditionalFormatRule) => void })
            .upsertConditionalFormatRule(rule);
        this.requestRender();
    }

    clearConditionalFormatsFromSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).clearConditionalFormats !== 'function') {
            throw new Error('GridController.clearConditionalFormatsFromSelection requires a spreadsheet engine streamer')
        }
        const activeSheetId = Number((this.streamer as { getActiveSheetId?: () => number | null }).getActiveSheetId?.() ?? 1) || 1;
        const sheetId = `sheet_${activeSheetId}`;
        (this.streamer as ViewportStreamer & { clearConditionalFormats: (range?: { sheet_id: string; start_row: number; start_col: number; end_row: number; end_col: number } | null) => void })
            .clearConditionalFormats({
                sheet_id: sheetId,
                start_row: startRow,
                start_col: startCol,
                end_row: maxRow,
                end_col: maxCol,
            });
        this.requestRender();
    }

    upsertSparklineGroup(group: SpreadsheetSparklineGroup) {
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).upsertSparklineGroup !== 'function') {
            throw new Error('GridController.upsertSparklineGroup requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { upsertSparklineGroup: (group: SpreadsheetSparklineGroup) => void })
            .upsertSparklineGroup(group);
        this.requestRender();
    }

    clearSparklinesFromSelection() {
        if (!this.selection) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const maxCol = Math.max(col, endCol);
        const startRow = Math.min(row, endRow);
        const maxRow = Math.max(row, endRow);
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).clearSparklines !== 'function') {
            throw new Error('GridController.clearSparklinesFromSelection requires a spreadsheet engine streamer')
        }
        const activeSheetId = Number((this.streamer as { getActiveSheetId?: () => number | null }).getActiveSheetId?.() ?? 1) || 1;
        const sheetId = `sheet_${activeSheetId}`;
        (this.streamer as ViewportStreamer & { clearSparklines: (range?: { sheet_id: string; start_row: number; start_col: number; end_row: number; end_col: number } | null) => void })
            .clearSparklines({
                sheet_id: sheetId,
                start_row: startRow,
                start_col: startCol,
                end_row: maxRow,
                end_col: maxCol,
            });
        this.requestRender();
    }

    deleteSparklineGroup(groupId: string) {
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).deleteSparklineGroup !== 'function') {
            throw new Error('GridController.deleteSparklineGroup requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { deleteSparklineGroup: (groupId: string) => void })
            .deleteSparklineGroup(groupId);
        this.requestRender();
    }

    groupSparklineLocations(groupId: string, locations?: SpreadsheetRangeAddress[], newGroupId?: string | null) {
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).groupSparklineLocations !== 'function') {
            throw new Error('GridController.groupSparklineLocations requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { groupSparklineLocations: (groupId: string, locations?: SpreadsheetRangeAddress[], newGroupId?: string | null) => void })
            .groupSparklineLocations(groupId, locations, newGroupId);
        this.requestRender();
    }

    ungroupSparklineGroup(groupId: string) {
        if (this.dataMode !== 'streamer' || !this.streamer || typeof (this.streamer as any).ungroupSparklineGroup !== 'function') {
            throw new Error('GridController.ungroupSparklineGroup requires a spreadsheet engine streamer')
        }
        (this.streamer as ViewportStreamer & { ungroupSparklineGroup: (groupId: string) => void })
            .ungroupSparklineGroup(groupId);
        this.requestRender();
    }

    private stopRenderLoop() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private startClipboardMarqueeLoop() {
        if (this.clipboardMarqueeAnimationFrame !== null) return;
        const tick = () => {
            if (!this.clipboardVisual) {
                this.clipboardMarqueeAnimationFrame = null;
                return;
            }
            this.renderDirect();
            this.clipboardMarqueeAnimationFrame = requestAnimationFrame(tick);
        };
        this.clipboardMarqueeAnimationFrame = requestAnimationFrame(tick);
    }

    private stopClipboardMarqueeLoop() {
        if (this.clipboardMarqueeAnimationFrame !== null) {
            cancelAnimationFrame(this.clipboardMarqueeAnimationFrame);
            this.clipboardMarqueeAnimationFrame = null;
        }
    }

    private normalizeSelection(selection: GridSelectionRange): GridSelectionRange {
        const startCol = Math.max(0, Math.min(selection.col, selection.endCol));
        const endCol = Math.max(0, Math.max(selection.col, selection.endCol));
        const startRow = Math.max(0, Math.min(selection.row, selection.endRow));
        const endRow = Math.max(0, Math.max(selection.row, selection.endRow));
        return { col: startCol, row: startRow, endCol, endRow };
    }

    private setClipboardVisual(mode: ClipboardVisualMode, selection: GridSelectionRange) {
        this.clipboardVisual = {
            mode,
            selection: this.normalizeSelection(selection),
            startedAtMs: performance.now(),
        };
        this.startClipboardMarqueeLoop();
        this.requestRender();
    }

    private clearClipboardVisual() {
        this.clipboardVisual = null;
        this.stopClipboardMarqueeLoop();
        this.requestRender();
    }

    requestRender() {
        if (this.renderPending) return;
        this.renderPending = true;
        this.animationFrameId = requestAnimationFrame(() => {
            this.renderPending = false;
            this.animationFrameId = null;
            this.render();
        });
    }

    renderDirect() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.renderPending = false;
        this.render();
    }

    render() {
        if (!this.renderer || !this.textRenderer) return;
        perf('controller.render', () => {
            const bounds = this.getVisibleBounds();
            const startX = this.getColumnOffset(bounds.startCol) - this.scrollX;
            const startY = this.getRowOffset(bounds.startRow) - this.scrollY;

            let selectionScreenRect: { x: number; y: number; w: number; h: number } | null = null;
            if (this.selection) {
                const minCol = Math.max(0, Math.min(this.selection.col, this.selection.endCol));
                const maxCol = Math.min(this.colCount - 1, Math.max(this.selection.col, this.selection.endCol));
                const minRow = Math.max(0, Math.min(this.selection.row, this.selection.endRow));
                const maxRow = Math.min(this.rowCount - 1, Math.max(this.selection.row, this.selection.endRow));

                selectionScreenRect = this.getRangeScreenRect({
                    start: { col: minCol, row: minRow },
                    end: { col: maxCol, row: maxRow },
                });
            }
            let clipboardSelectionScreenRect: { x: number; y: number; w: number; h: number } | null = null;
            if (this.clipboardVisual) {
                const selection = this.clipboardVisual.selection;
                clipboardSelectionScreenRect = this.getRangeScreenRect({
                    start: { col: selection.col, row: selection.row },
                    end: { col: selection.endCol, row: selection.endRow },
                });
            }
            const dragPreview = this.getDragPreviewScreenRect();

            const viewState: ViewState = {
                scrollX: this.scrollX,
                scrollY: this.scrollY,
                zoom: this.zoom,
                viewportWidth: this.viewportWidth,
                viewportHeight: this.viewportHeight,
                selection: this.selection,
                frozenRowsTop: this.frozenRowsTop,
                frozenColsLeft: this.frozenColsLeft,
                startCol: bounds.startCol,
                endCol: bounds.endCol,
                startRow: bounds.startRow,
                endRow: bounds.endRow,
                startX,
                startY,
                selectionScreenRect,
                clipboardSelectionScreenRect,
                clipboardMode: this.clipboardVisual?.mode ?? null,
                dragPreviewScreenRect: dragPreview?.rect ?? null,
                dragPreviewMode: dragPreview?.mode ?? null,
                marqueeOffset: this.clipboardVisual
                    ? -(((performance.now() - this.clipboardVisual.startedAtMs) / 120) % 7)
                    : -((performance.now() / 120) % 7),
                gridlinesVisible: this.gridlinesVisible,
                sheetProtection: this.sheetProtectionProjection?.enabled === true ? { enabled: true } : null,
            };

            // 1. Draw Grid & Selection
            this.renderer!.render(
                viewState,
                this.getColumnWidth.bind(this),
                this.getRowHeight.bind(this),
                this.getCellFormat.bind(this),
                this.getCellMetadata.bind(this),
                this.colCount,
                this.rowCount
            );

            // 2. Draw Text
            this.textRenderer!.render(
                viewState,
                this.getColumnWidth.bind(this),
                this.getRowHeight.bind(this),
                this.getCellValue.bind(this),
                this.getCellFormat.bind(this),
                this.colCount,
                this.rowCount
            );
        });
    }

    setSharedState(sab: SharedArrayBuffer | ArrayBuffer | null) {
        if (!sab) {
            this.sharedState = null;
            return;
        }
        this.sharedState = new SharedState(sab);
        // Sync initial layout if needed
    }

    private getColumnBaseWidth(col: number): number {
        if (this.sharedState) {
            const w = this.sharedState.getColWidth(col);
            if (w > 0) return w;
        }
        return this.colWidths.get(col) ?? GridController.DEFAULT_COL_WIDTH;
    }

    private getRowBaseHeight(row: number): number {
        if (this.sharedState) {
            const h = this.sharedState.getRowHeight(row);
            if (h > 0) return h;
        }
        return this.rowHeights.get(row) ?? GridController.DEFAULT_ROW_HEIGHT;
    }

    getColumnWidth(col: number): number {
        return this.getColumnBaseWidth(col) * this.zoom;
    }

    getRowHeight(row: number): number {
        return this.getRowBaseHeight(row) * this.zoom;
    }

    getColumnLogicalWidth(col: number): number {
        return this.getColumnBaseWidth(col);
    }

    getRowLogicalHeight(row: number): number {
        return this.getRowBaseHeight(row);
    }





    resize(width: number, height: number) {
        this.viewportWidth = width;
        this.viewportHeight = height;

        if (this.renderer) {
            this.renderer.resize(width, height);
        }

        if (this.sharedState) {
            this.sharedState.setViewport(width, height);
        }
        this.updateStreamerViewport();

        // Render synchronously to avoid black flash
        this.render();
        if (this.streamer) {
            this.streamer.setViewport(width, height);
        }

        if (this.onResize) {
            this.onResize(width, height);
        }
    }

    setSelection(selection: { col: number, row: number, endCol: number, endRow: number } | null, options?: { force?: boolean }): boolean {
        if (selection && !options?.force && !this.isSelectionSelectableBySheetProtection(selection)) {
            return false;
        }
        this.selection = selection;
        if (selection && this.streamer && typeof this.streamer.setSelection === 'function') {
            // Sync anchor to WASM for formula/clipboard parity (use anchor/top-left)
            const anchorCol = Math.min(selection.col, selection.endCol);
            const anchorRow = Math.min(selection.row, selection.endRow);
            const leadCol = Math.max(selection.col, selection.endCol);
            const leadRow = Math.max(selection.row, selection.endRow);
            this.streamer.setSelection(anchorCol, anchorRow, leadCol, leadRow);
        }
        return true;
    }

    selectColumnRange(anchorCol: number, leadCol: number): boolean {
        if (this.colCount <= 0 || this.rowCount <= 0) return false;
        const clampCol = (value: number) => Math.max(0, Math.min(this.colCount - 1, Math.floor(Number(value) || 0)));
        const selected = this.setSelection({
            col: clampCol(anchorCol),
            row: 0,
            endCol: clampCol(leadCol),
            endRow: this.rowCount - 1,
        });
        if (!selected) return false;
        this.notifySelectionChange();
        this.requestRender();
        return true;
    }

    selectRowRange(anchorRow: number, leadRow: number): boolean {
        if (this.colCount <= 0 || this.rowCount <= 0) return false;
        const clampRow = (value: number) => Math.max(0, Math.min(this.rowCount - 1, Math.floor(Number(value) || 0)));
        const selected = this.setSelection({
            col: 0,
            row: clampRow(anchorRow),
            endCol: this.colCount - 1,
            endRow: clampRow(leadRow),
        });
        if (!selected) return false;
        this.notifySelectionChange();
        this.requestRender();
        return true;
    }

    selectAllCells(): boolean {
        if (this.colCount <= 0 || this.rowCount <= 0) return false;
        const selected = this.setSelection({
            col: 0,
            row: 0,
            endCol: this.colCount - 1,
            endRow: this.rowCount - 1,
        });
        if (!selected) return false;
        this.notifySelectionChange();
        this.requestRender();
        return true;
    }

    setSelectionChangeCallback(cb: (selection: { col: number, row: number, endCol: number, endRow: number } | null) => void) {
        this.onSelectionChange = cb;
    }

    setPasteOptionsCallback(cb: ((context: PasteOptionsContext) => void) | null) {
        this.onPasteOptions = cb;
    }

    setFillOptionsCallback(cb: ((context: FillOptionsContext) => void) | null) {
        this.onFillOptions = cb;
    }

    setValidationDropdownCallback(cb: (() => void) | null) {
        this.onValidationDropdownRequest = cb;
    }

    setValidationDropdownAvailable(available: boolean) {
        this.validationDropdownAvailable = available;
    }

    setOpenHyperlinkCallback(cb: ((context: CellHyperlinkActivationContext) => void) | null) {
        this.onOpenHyperlinkRequest = cb;
    }

    setFormulaRangeSelectionCallback(cb: GridFormulaRangeSelectionHandler | null) {
        this.formulaRangeSelectionHandler = cb;
    }

    private isHyperlinkActivationEvent(event: MouseEvent): boolean {
        return event.button === 0 && (event.ctrlKey || event.metaKey);
    }

    private getCellHyperlinkForActivation(col: number, row: number): string | null {
        const hyperlink = String(this.getCellMetadata(col, row)?.hyperlink ?? '').trim();
        return hyperlink || null;
    }

    private requestOpenHyperlinkForCell(col: number, row: number): boolean {
        if (!this.onOpenHyperlinkRequest) return false;
        const hyperlink = this.getCellHyperlinkForActivation(col, row);
        if (!hyperlink) return false;
        this.onOpenHyperlinkRequest({ col, row, hyperlink });
        return true;
    }

    private dispatchFormulaRangeSelection(
        phase: GridFormulaRangeSelectionPhase,
        range: GridFormulaRangeCoord,
        event: MouseEvent,
    ): boolean {
        if (!this.formulaRangeSelectionHandler) return false;
        return this.formulaRangeSelectionHandler({
            phase,
            range,
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            altKey: event.altKey,
        }) === true;
    }

    setScrollChangeCallback(cb: (x: number, y: number) => void) {
        this.onScrollChange = cb;
    }

    setScroll(x: number, y: number, opts?: { renderMode?: 'defer' | 'direct' }) {
        const newX = Math.max(0, x);
        const newY = Math.max(0, y);

        // Use tighter threshold to prevent scroll lag
        if (Math.abs(newX - this.scrollX) < 0.01 && Math.abs(newY - this.scrollY) < 0.01) {
            return;
        }

        this.scrollX = newX;
        this.scrollY = newY;

        if (this.sharedState) {
            this.sharedState.setScroll(this.scrollX, this.scrollY);
        }

        if (this.streamer) {
            this.streamer.setScroll(this.scrollX, this.scrollY);
        }

        // Notify callback (e.g. sync headers) - do this FIRST before render to keep headers in sync
        if (this.onScrollChange) {
            this.onScrollChange(this.scrollX, this.scrollY);
        }

        this.updateStreamerViewport();
        if (opts?.renderMode === 'direct') {
            this.renderDirect();
            return;
        }
        this.requestRender();
    }

    private updateStreamerViewport() {
        if (this.streamer) {
            perf('controller.updateStreamerViewport', () => {
                this.streamer!.updateViewport(this.scrollX, this.scrollY, this.viewportWidth, this.viewportHeight);
            });
        }

        // Keep scroll clamped after updates from streamer/render cycle
        this.clampScrollTargets();
    }

    private setupEventListeners() {
        if (!this.inputTarget) return;
        this.inputTarget.addEventListener('mousedown', this.handleMouseDown);
        window.addEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true });
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
        window.addEventListener('keydown', this.handleKeyDown, { capture: true });
        this.inputTarget.addEventListener('dblclick', this.handleDoubleClick);
        window.addEventListener('copy', this.handleCopy);
        window.addEventListener('cut', this.handleCut);
        window.addEventListener('paste', this.handlePaste);
        this.inputTarget.addEventListener('contextmenu', this.handleContextMenu);
    }

    private removeEventListeners() {
        if (!this.inputTarget) return;
        this.inputTarget.removeEventListener('mousedown', this.handleMouseDown);
        window.removeEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true } as any);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('keydown', this.handleKeyDown, { capture: true });
        this.inputTarget.removeEventListener('dblclick', this.handleDoubleClick);
        window.removeEventListener('copy', this.handleCopy);
        window.removeEventListener('cut', this.handleCut);
        window.removeEventListener('paste', this.handlePaste);
        this.inputTarget.removeEventListener('contextmenu', this.handleContextMenu);
    }

    private onContextMenu: ((x: number, y: number, col: number, row: number) => void) | null = null;

    setContextMenuCallback(callback: (x: number, y: number, col: number, row: number) => void) {
        this.onContextMenu = callback;
    }

    handleContextMenu = (e: MouseEvent) => {
        if (isGridContextMenuOwnedByOverlay(e.target)) return;
        e.preventDefault();
        if (!this.canvas) return;

        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const cell = this.hitTest(x, y);
        if (cell) {
            // If right-clicking outside selection, select the cell
            if (!this.isCellSelected(cell.col, cell.row)) {
                const selected = this.setSelection({
                    col: cell.col,
                    row: cell.row,
                    endCol: cell.col,
                    endRow: cell.row
                });
                if (selected) {
                    this.notifySelectionChange();
                    this.requestRender();
                }
            }

            if (this.onContextMenu) {
                this.onContextMenu(e.clientX, e.clientY, cell.col, cell.row);
            }
        }
    }

    private isCellSelected(col: number, row: number): boolean {
        if (!this.selection) return false;
        const minCol = Math.min(this.selection.col, this.selection.endCol);
        const maxCol = Math.max(this.selection.col, this.selection.endCol);
        const minRow = Math.min(this.selection.row, this.selection.endRow);
        const maxRow = Math.max(this.selection.row, this.selection.endRow);
        return col >= minCol && col <= maxCol && row >= minRow && row <= maxRow;
    }

    handleCopy = async (e: ClipboardEvent) => {
        // Don't hijack copy unless the grid is focused (otherwise Cmd/Ctrl+C should copy selected text elsewhere).
        if (!this.hasFocus) return;
        if (this.isEditableTarget(document.activeElement) || this.isEditableTarget(e.target)) return;
        if (!this.selection || !this.streamer) return;
        e.preventDefault();
        this.performCopy();
    }

    handleCut = async (e: ClipboardEvent) => {
        if (!this.hasFocus) return;
        if (this.isEditableTarget(document.activeElement) || this.isEditableTarget(e.target)) return;
        if (!this.selection || !this.streamer) return;
        e.preventDefault();
        this.performCut();
    }

    handlePaste = (e: ClipboardEvent) => {
        if (!this.hasFocus) return;
        if (this.isEditableTarget(document.activeElement) || this.isEditableTarget(e.target)) return;
        if (!this.selection || !this.streamer) return;
        e.preventDefault();

        const text = e.clipboardData?.getData('text/plain');
        if (text) {
            this.pasteText(text);
        }
    }

    async performCopy(): Promise<boolean> {
        if (!this.selection || !this.streamer) return false;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const startRow = Math.min(row, endRow);
        const finalCol = Math.max(col, endCol);
        const finalRow = Math.max(row, endRow);
        const copiedSelection = { col: startCol, row: startRow, endCol: finalCol, endRow: finalRow };

        try {
            const tsv = await this.streamer.copyRangeTsv(startCol, startRow, finalCol, finalRow);
            await navigator.clipboard.writeText(tsv);
            this.setClipboardVisual('copy', copiedSelection);
            return true;
        } catch (err) {
            console.error('Failed to copy:', err);
            return false;
        }
    }

    async performCut() {
        if (!this.selection || !this.streamer) return;
        const { col, row, endCol, endRow } = this.selection;
        const startCol = Math.min(col, endCol);
        const startRow = Math.min(row, endRow);
        const finalCol = Math.max(col, endCol);
        const finalRow = Math.max(row, endRow);

        try {
            // Copy first
            const copied = await this.performCopy();
            if (!copied) return;

            // Then clear
            this.streamer.clearRange(startCol, startRow, finalCol - startCol + 1, finalRow - startRow + 1);
            this.setClipboardVisual('cut', { col: startCol, row: startRow, endCol: finalCol, endRow: finalRow });
            this.updateStreamerViewport();
            this.requestRender();
        } catch (err) {
            console.error('Failed to cut:', err);
        }
    }

    async performPaste() {
        if (!this.selection || !this.streamer) return;
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                this.pasteText(text);
            }
        } catch (err) {
            console.error('Failed to paste:', err);
        }
    }

    hasEngineClipboard(): boolean {
        return Boolean(this.streamer?.hasEngineClipboard?.());
    }

    performPasteSpecial(mode: SpreadsheetPasteSpecialMode): boolean {
        if (!this.selection) return false;
        return this.performPasteSpecialAt(this.selection.col, this.selection.row, mode);
    }

    performPasteSpecialAt(col: number, row: number, mode: SpreadsheetPasteSpecialMode): boolean {
        if (!this.streamer) return false;
        const targetCol = Math.max(0, Math.floor(Number(col) || 0));
        const targetRow = Math.max(0, Math.floor(Number(row) || 0));
        const shape = this.streamer.getEngineClipboardPasteShape?.(mode) ?? null;
        if (shape) {
            this.expandGridForPastedShape(targetCol, targetRow, shape.rowCount, shape.colCount);
        }
        const didPaste = this.streamer.pasteSpecialFromLastCopy?.(targetCol, targetRow, mode) ?? false;
        if (!didPaste) return false;
        this.clearClipboardVisual();
        this.updateStreamerViewport();
        this.requestRender();
        this.emitPasteOptions(targetCol, targetRow, shape?.rowCount ?? 1, shape?.colCount ?? 1);
        return true;
    }

    private emitPasteOptions(targetCol: number, targetRow: number, rowCount: number, colCount: number) {
        if (!this.onPasteOptions) return;
        this.onPasteOptions({
            targetCol: Math.max(0, Math.floor(Number(targetCol) || 0)),
            targetRow: Math.max(0, Math.floor(Number(targetRow) || 0)),
            rowCount: Math.max(1, Math.floor(Number(rowCount) || 1)),
            colCount: Math.max(1, Math.floor(Number(colCount) || 1)),
            canPasteSpecial: this.hasEngineClipboard(),
        });
    }

    performFillOptionAt(context: FillOptionsContext, mode: SpreadsheetFillRangeMode): boolean {
        if (!this.streamer) return false;
        const normalized = this.normalizeFillOptionsContext(context);
        this.streamer.fillData(
            normalized.sourceCol,
            normalized.sourceRow,
            normalized.sourceWidth,
            normalized.sourceHeight,
            normalized.targetCol,
            normalized.targetRow,
            normalized.targetWidth,
            normalized.targetHeight,
            mode,
        );
        this.updateStreamerViewport();
        this.requestRender();
        this.emitFillOptions(normalized);
        return true;
    }

    private normalizeFillOptionsContext(context: FillOptionsContext): FillOptionsContext {
        return {
            sourceCol: Math.max(0, Math.floor(Number(context.sourceCol) || 0)),
            sourceRow: Math.max(0, Math.floor(Number(context.sourceRow) || 0)),
            sourceWidth: Math.max(1, Math.floor(Number(context.sourceWidth) || 1)),
            sourceHeight: Math.max(1, Math.floor(Number(context.sourceHeight) || 1)),
            targetCol: Math.max(0, Math.floor(Number(context.targetCol) || 0)),
            targetRow: Math.max(0, Math.floor(Number(context.targetRow) || 0)),
            targetWidth: Math.max(1, Math.floor(Number(context.targetWidth) || 1)),
            targetHeight: Math.max(1, Math.floor(Number(context.targetHeight) || 1)),
            canFillSeries: Boolean(context.canFillSeries),
        };
    }

    private emitFillOptions(context: FillOptionsContext) {
        if (!this.onFillOptions) return;
        this.onFillOptions(this.normalizeFillOptionsContext(context));
    }

    private expandGridForPastedShape(col: number, row: number, rowCount: number, colCount: number) {
        if (!this.streamer) return;
        const safeRowCount = Math.max(0, Math.floor(Number(rowCount) || 0));
        const safeColCount = Math.max(0, Math.floor(Number(colCount) || 0));
        if (safeRowCount <= 0 || safeColCount <= 0) return;
        // Auto-Expand Grid Logic
        const neededRows = row + safeRowCount;
        const neededCols = col + safeColCount;

        let dimChanged = false;
        let newRowCount = this.rowCount;
        let newColCount = this.colCount;

        if (neededRows > this.rowCount) {
            newRowCount = neededRows;
            dimChanged = true;
        }
        if (neededCols > this.colCount) {
            newColCount = neededCols;
            dimChanged = true;
        }

        if (dimChanged) {
            this.colCount = newColCount;
            this.rowCount = newRowCount;
            this.invalidateCache();
            if (this.onRowCountChange) this.onRowCountChange(this.rowCount);
            this.streamer.setDimensions(this.colCount, this.rowCount);
            // Ensure resize callback fires to update headers
            if (this.onResize) this.onResize(this.viewportWidth, this.viewportHeight); // Trigger UI update?
        }
    }

    private pasteText(text: string) {
        if (!this.selection || !this.streamer) return;

        // Lightweight parsing for dimensions only (avoid full 2D array creation on main thread)
        const lines = text.split(/\r\n|\n|\r/);
        // If last row is empty (trailing newline), remove it
        if (lines.length > 0 && lines[lines.length - 1] === '') {
            lines.pop();
        }
        if (lines.length === 0) return;

        const rowCount = lines.length;

        // Optimize: Assume rectangular data (typical for spreadsheet copy) and check first row for columns.
        // Checking every row on main thread is too expensive for large datasets.
        let maxCols = 0;
        if (rowCount > 0) {
            maxCols = lines[0].split('\t').length;
        }

        const { col, row } = this.selection;
        this.expandGridForPastedShape(col, row, rowCount, maxCols);

        // Paste starting at anchor - send RAW text to worker to avoid main thread freeze
        this.streamer.pasteData(col, row, text);
        this.clearClipboardVisual();
        this.updateStreamerViewport();
        this.requestRender();
        this.emitPasteOptions(col, row, rowCount, maxCols);
    }

    // Wheel is handled by the scroll container; leave this as a no-op to avoid main-thread wheel delays.
    handleWheel = (_e: WheelEvent) => { }

    // Format Painter State
    formatPaintMode = false;
    formatPaintSource: any = null;
    formatPaintSourceRange: GridSelectionRange | null = null;

    handleMouseDown = (e: MouseEvent) => {
        if (!this.canvas) return;
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const cell = this.hitTest(x, y);

        if (e.button === 0 && cell) {
            const range = { start: { col: cell.col, row: cell.row }, end: { col: cell.col, row: cell.row } };
            if (this.dispatchFormulaRangeSelection('start', range, e)) {
                e.preventDefault();
                e.stopPropagation();
                this.isDragging = true;
                this.dragMode = 'formula-range';
                this.dragStart = { x, y, col: cell.col, row: cell.row };
                this.dragCurrent = { x, y, col: cell.col, row: cell.row };
                this.requestRender();
                return;
            }

            if (this.isHyperlinkActivationEvent(e) && this.requestOpenHyperlinkForCell(cell.col, cell.row)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        }

        if (e.button === 0) {
            const tableEdgeHit = this.getTableEdgeHitAtPoint(x, y);
            if (tableEdgeHit && this.selectTable(tableEdgeHit)) {
                e.preventDefault();
                e.stopPropagation();
                this.isDragging = false;
                this.dragMode = 'none';
                return;
            }
        }

        this.isDragging = true;

        // Format Painter Logic
        if (this.formatPaintMode && this.formatPaintSource) {
            if (cell) {
                if (!this.canFormatSelectionBySheetProtection()) {
                    this.formatPaintMode = false;
                    this.formatPaintSource = null;
                    this.formatPaintSourceRange = null;
                    if (this.inputTarget) this.inputTarget.style.cursor = 'default';
                    return;
                }
                const sourceRange = this.formatPaintSourceRange ?? { col: cell.col, row: cell.row, endCol: cell.col, endRow: cell.row };
                const sourceWidth = sourceRange.endCol - sourceRange.col + 1;
                const sourceHeight = sourceRange.endRow - sourceRange.row + 1;
                if (this.dataMode === 'streamer' && this.streamer && typeof this.streamer.copyRangeFormats === 'function') {
                    this.streamer.copyRangeFormats(
                        sourceRange.col,
                        sourceRange.row,
                        sourceWidth,
                        sourceHeight,
                        cell.col,
                        cell.row,
                        sourceWidth,
                        sourceHeight,
                    );
                } else if (this.dataMode === 'local') {
                    this.setLocalPreviewCellFormat(cell.col, cell.row, this.formatPaintSource);
                } else {
                    this.setCellFormat(cell.col, cell.row, this.formatPaintSource);
                }
                this.formatPaintMode = false;
                this.formatPaintSource = null;
                this.formatPaintSourceRange = null;
                // Update selection to the clicked cell
                const selected = this.setSelection({
                    col: cell.col,
                    row: cell.row,
                    endCol: cell.col + sourceWidth - 1,
                    endRow: cell.row + sourceHeight - 1
                });
                if (selected) {
                    this.notifySelectionChange();
                    this.requestRender();
                }
                if (this.inputTarget) this.inputTarget.style.cursor = 'default';
                return;
            }
        }

        // Check cursor to determine mode
        // Ideally we should re-calculate logic to be safe, but cursor is a good proxy if updated correctly
        const cursor = this.inputTarget?.style.cursor;

        if (cursor === 'crosshair' && this.selection) {
            this.dragMode = 'fill';
            this.dragStart = { x, y, col: this.selection.endCol, row: this.selection.endRow }; // Start from end of selection
            this.dragCurrent = { x, y, col: this.selection.endCol, row: this.selection.endRow };
        } else if ((cursor === 'grab' || cursor === 'copy') && this.selection) {
            this.dragMode = (cursor === 'copy' || e.altKey || e.ctrlKey || e.metaKey) ? 'copy' : 'move';
            // For move, we track the cell under mouse
            const cell = this.hitTest(x, y);
            if (cell) {
                this.dragStart = { x, y, col: cell.col, row: cell.row };
                this.dragCurrent = { x, y, col: cell.col, row: cell.row };
            }
        } else {
            this.dragMode = 'none';
            if (cell) {
                // Shift+Click: Extend selection
                let selected = false;
                if (e.shiftKey && this.selection) {
                    selected = this.setSelection({
                        ...this.selection,
                        endCol: cell.col,
                        endRow: cell.row
                    });
                } else {
                    // New selection
                    selected = this.setSelection({
                        col: cell.col,
                        row: cell.row,
                        endCol: cell.col,
                        endRow: cell.row
                    });
                }
                if (selected) {
                    this.notifySelectionChange();
                    this.requestRender();
                }
            }
        }
    }

    activateFormatPainter() {
        if (this.selection) {
            // Copy format from active cell (top-left of selection)
            const sourceRange = this.normalizeSelection(this.selection);
            this.formatPaintSource = this.getAuthoringCellFormat(sourceRange.col, sourceRange.row) ?? {};
            this.formatPaintSourceRange = sourceRange;
            this.formatPaintMode = true;
            if (this.inputTarget) this.inputTarget.style.cursor = 'cell'; // Or alias? 'copy'?
        }
    }

    autoSizeColumn(col: number) {
        if (!this.textRenderer) return;

        let maxWidth = 50; // Min width

        // Heuristic: Check first 100 rows + Visible rows
        // This avoids scanning millions of rows
        const rowsToCheck = new Set<number>();
        for (let r = 0; r < 100; r++) rowsToCheck.add(r);

        const bounds = this.getVisibleBounds();
        for (let r = bounds.startRow; r <= bounds.endRow; r++) rowsToCheck.add(r);

        for (const r of rowsToCheck) {
            if (r >= this.rowCount) break;
            const val = this.getCellValue(col, r);
            if (val == null || String(val) === '') continue;
            const format = this.getCellFormat(col, r);
            const measurement = typeof this.textRenderer.measureCellText === 'function'
                ? this.textRenderer.measureCellText(String(val), format)
                : { width: this.textRenderer.measureText(String(val), format) + 12 };
            maxWidth = Math.max(maxWidth, measurement.width);
        }

        this.setColumnWidth(col, maxWidth, { displaySize: true });
        this.requestRender();
    }

    autoSizeSelectedColumns(): boolean {
        if (!this.selection) return false;
        const selection = this.normalizeSelection(this.selection);
        for (let col = selection.col; col <= selection.endCol; col++) {
            this.autoSizeColumn(col);
        }
        return true;
    }

    autoSizeRow(row: number) {
        if (!this.textRenderer) return;

        let maxHeight = 24;
        const bounds = this.getVisibleBounds();
        const startCol = Math.max(0, Math.min(bounds.startCol, this.colCount - 1));
        const endCol = Math.max(startCol, Math.min(bounds.endCol, this.colCount - 1));
        const colsToCheck = new Set<number>();
        for (let c = 0; c < Math.min(40, this.colCount); c++) colsToCheck.add(c);
        for (let c = startCol; c <= endCol; c++) colsToCheck.add(c);

        for (const c of colsToCheck) {
            if (c >= this.colCount) break;
            const val = this.getCellValue(c, row);
            if (val == null || String(val) === '') continue;
            const format = this.getCellFormat(c, row);
            const mergeSpan = Math.max(1, Math.floor(Number(format?.mergeColSpan) || 1));
            let cellWidth = 0;
            for (let spanCol = 0; spanCol < mergeSpan && c + spanCol < this.colCount; spanCol++) {
                cellWidth += this.getColumnWidth(c + spanCol);
            }
            const measurement = typeof this.textRenderer.measureCellText === 'function'
                ? this.textRenderer.measureCellText(String(val), format, cellWidth || this.getColumnWidth(c))
                : {
                    height: (Math.round((Math.max(1, Number(format?.fontSize || 13) || 13)) * 1.25)
                        * Math.max(1, String(val).split(/\r?\n|\n|\r/).length)) + 8,
                };
            maxHeight = Math.max(maxHeight, measurement.height);
        }

        this.setRowHeight(row, maxHeight, { displaySize: true });
        this.requestRender();
    }

    autoSizeSelectedRows(): boolean {
        if (!this.selection) return false;
        const selection = this.normalizeSelection(this.selection);
        for (let row = selection.row; row <= selection.endRow; row++) {
            this.autoSizeRow(row);
        }
        return true;
    }

    handleMouseMove = (e: MouseEvent) => {
        if (!this.canvas) return;
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (this.isDragging) {
            // Dragging logic
            if (this.dragMode === 'formula-range') {
                const cell = this.hitTest(x, y);
                if (cell && this.dragStart) {
                    this.dragCurrent = { x, y, col: cell.col, row: cell.row };
                    const consumed = this.dispatchFormulaRangeSelection('update', {
                        start: { col: this.dragStart.col, row: this.dragStart.row },
                        end: { col: cell.col, row: cell.row },
                    }, e);
                    if (consumed) {
                        e.preventDefault();
                        this.requestRender();
                    }
                }
            } else if (this.dragMode === 'move' || this.dragMode === 'copy' || this.dragMode === 'fill') {
                const cell = this.hitTest(x, y);
                if (cell) {
                    this.dragCurrent = { x, y, col: cell.col, row: cell.row };
                    this.requestRender();
                }
            } else {
                // Selection drag
                const cell = this.hitTest(x, y);
                if (cell && this.selection) {
                    if (this.selection.endCol !== cell.col || this.selection.endRow !== cell.row) {
                        const selected = this.setSelection({
                            ...this.selection,
                            endCol: cell.col,
                            endRow: cell.row,
                        });
                        if (selected) {
                            this.notifySelectionChange();
                            this.requestRender();
                        }
                    }
                }
            }
        } else {
            // Cursor update logic
            const hoverCell = this.hitTest(x, y);
            if (
                hoverCell &&
                (e.ctrlKey || e.metaKey) &&
                this.getCellHyperlinkForActivation(hoverCell.col, hoverCell.row)
            ) {
                if (this.inputTarget) this.inputTarget.style.cursor = 'pointer';
                return;
            }

            if (this.selection) {
                const { col, row, endCol, endRow } = this.selection;
                // Get bounds of selection
                const startCol = Math.min(col, endCol);
                const startRow = Math.min(row, endRow);
                const finalCol = Math.max(col, endCol);
                const finalRow = Math.max(row, endRow);

                const selectionRect = this.getRangeScreenRect({
                    start: { col: startCol, row: startRow },
                    end: { col: finalCol, row: finalRow },
                });
                if (!selectionRect) {
                    if (this.inputTarget) this.inputTarget.style.cursor = 'default';
                    return;
                }
                const x1 = selectionRect.x;
                const y1 = selectionRect.y;
                const x2 = x1 + selectionRect.w;
                const y2 = y1 + selectionRect.h;

                // Check fill handle (bottom-right corner)
                if (Math.abs(x - x2) < 8 && Math.abs(y - y2) < 8) {
                    if (this.inputTarget) this.inputTarget.style.cursor = 'crosshair';
                    return;
                }

                // Check border (grab)
                const onBorder = (Math.abs(x - x1) < 5 && y >= y1 && y <= y2) ||
                    (Math.abs(x - x2) < 5 && y >= y1 && y <= y2) ||
                    (Math.abs(y - y1) < 5 && x >= x1 && x <= x2) ||
                    (Math.abs(y - y2) < 5 && x >= x1 && x <= x2);

                if (onBorder) {
                    if (this.inputTarget) this.inputTarget.style.cursor = (e.altKey || e.ctrlKey || e.metaKey) ? 'copy' : 'grab';
                    return;
                }
            }
            if (this.getTableEdgeHitAtPoint(x, y)) {
                if (this.inputTarget) this.inputTarget.style.cursor = 'pointer';
                return;
            }
            if (this.inputTarget) this.inputTarget.style.cursor = 'default';
        }
    }

    // Helper methods for geometry

    getViewport(): { width: number, height: number } {
        return { width: this.viewportWidth, height: this.viewportHeight };
    }

    getScroll(): { x: number, y: number } {
        return { x: this.scrollX, y: this.scrollY };
    }

    getCellAtClientPoint(clientX: number, clientY: number): GridFormulaCellCoord | null {
        if (!this.canvas) return null;
        const rect = this.canvas.getBoundingClientRect();
        return this.hitTest(clientX - rect.left, clientY - rect.top);
    }

    getRangeScreenRect(range: GridFormulaRangeCoord): GridScreenRect | null {
        if (this.colCount <= 0 || this.rowCount <= 0) return null;
        const minCol = Math.max(0, Math.min(range.start.col, range.end.col));
        const maxCol = Math.min(this.colCount - 1, Math.max(range.start.col, range.end.col));
        const minRow = Math.max(0, Math.min(range.start.row, range.end.row));
        const maxRow = Math.min(this.rowCount - 1, Math.max(range.start.row, range.end.row));
        if (maxCol < minCol || maxRow < minRow) return null;
        const x1 = this.getColumnOffset(minCol);
        const y1 = this.getRowOffset(minRow);
        const x2 = this.getColumnOffset(maxCol + 1);
        const y2 = this.getRowOffset(maxRow + 1);
        const frozenWidth = this.getFrozenWidth();
        const frozenHeight = this.getFrozenHeight();
        const pinnedX = this.frozenColsLeft > 0 && maxCol < this.frozenColsLeft && x2 <= frozenWidth;
        const pinnedY = this.frozenRowsTop > 0 && maxRow < this.frozenRowsTop && y2 <= frozenHeight;
        return {
            x: pinnedX ? x1 : x1 - this.scrollX,
            y: pinnedY ? y1 : y1 - this.scrollY,
            w: x2 - x1,
            h: y2 - y1,
        };
    }

    private getRangeWidth(startCol: number, endCol: number): number {
        let width = 0;
        for (let i = startCol; i <= endCol; i++) width += this.getColumnWidth(i);
        return width;
    }

    private getRangeHeight(startRow: number, endRow: number): number {
        let height = 0;
        for (let i = startRow; i <= endRow; i++) height += this.getRowHeight(i);
        return height;
    }

    private getDragPreviewScreenRect(): { mode: DragVisualMode; rect: GridScreenRect } | null {
        if (!this.selection || !this.dragStart || !this.dragCurrent) return null;
        if (this.dragMode !== 'move' && this.dragMode !== 'copy' && this.dragMode !== 'fill') return null;

        const normalized = this.normalizeSelection(this.selection);
        const width = normalized.endCol - normalized.col + 1;
        const height = normalized.endRow - normalized.row + 1;

        if (this.dragMode === 'move' || this.dragMode === 'copy') {
            const dCol = this.dragCurrent.col - this.dragStart.col;
            const dRow = this.dragCurrent.row - this.dragStart.row;
            if (dCol === 0 && dRow === 0) return null;
            const destCol = normalized.col + dCol;
            const destRow = normalized.row + dRow;
            if (destCol < 0 || destRow < 0) return null;
            const rect = this.getRangeScreenRect({
                start: { col: destCol, row: destRow },
                end: { col: destCol + width - 1, row: destRow + height - 1 },
            });
            return rect ? { mode: this.dragMode, rect } : null;
        }

        const dCol = this.dragCurrent.col - normalized.endCol;
        const dRow = this.dragCurrent.row - normalized.endRow;
        if (dCol > 0 && dCol >= dRow) {
            const rect = this.getRangeScreenRect({
                start: { col: normalized.col, row: normalized.row },
                end: { col: normalized.endCol + dCol, row: normalized.endRow },
            });
            return rect ? { mode: 'fill', rect } : null;
        }
        if (dRow > 0) {
            const rect = this.getRangeScreenRect({
                start: { col: normalized.col, row: normalized.row },
                end: { col: normalized.endCol, row: normalized.endRow + dRow },
            });
            return rect ? { mode: 'fill', rect } : null;
        }
        return null;
    }

    handleMouseUp = (_e: MouseEvent) => {
        if (!this.isDragging) return;

        if (this.dragMode === 'formula-range' && this.dragStart && this.dragCurrent) {
            this.dispatchFormulaRangeSelection('commit', {
                start: { col: this.dragStart.col, row: this.dragStart.row },
                end: { col: this.dragCurrent.col, row: this.dragCurrent.row },
            }, _e);
            _e.preventDefault();
            this.isDragging = false;
            this.dragMode = 'none';
            this.dragStart = null;
            this.dragCurrent = null;
            this.requestRender();
            return;
        }

        this.isDragging = false;

        if ((this.dragMode === 'move' || this.dragMode === 'copy') && this.dragStart && this.dragCurrent && this.selection && this.streamer) {
            const dCol = this.dragCurrent.col - this.dragStart.col;
            const dRow = this.dragCurrent.row - this.dragStart.row;

            if (dCol !== 0 || dRow !== 0) {
                const { col, row, endCol, endRow } = this.selection;
                const startCol = Math.min(col, endCol);
                const startRow = Math.min(row, endRow);
                const width = Math.abs(endCol - col) + 1;
                const height = Math.abs(endRow - row) + 1;

                const destCol = startCol + dCol;
                const destRow = startRow + dRow;

                if (destCol >= 0 && destRow >= 0) {
                    if (this.dragMode === 'copy') {
                        this.streamer.copyData(startCol, startRow, width, height, destCol, destRow);
                    } else {
                        this.streamer.moveData(startCol, startRow, width, height, destCol, destRow);
                    }
                    this.updateStreamerViewport();

                    // Update selection
                    this.setSelection({
                        col: destCol,
                        row: destRow,
                        endCol: destCol + width - 1,
                        endRow: destRow + height - 1
                    });
                    this.notifySelectionChange();
                }
            }
        } else if (this.dragMode === 'fill' && this.dragStart && this.dragCurrent && this.selection && this.streamer) {
            // Fill logic
            const { col, row, endCol, endRow } = this.selection;
            const startCol = Math.min(col, endCol);
            const startRow = Math.min(row, endRow);
            const srcW = Math.abs(endCol - col) + 1;
            const srcH = Math.abs(endRow - row) + 1;

            // Determine direction
            // We are dragging from endCol, endRow
            // If dragCurrent.col > endCol, fill right
            // If dragCurrent.row > endRow, fill down

            // Simple logic: Fill down or right based on max delta
            const dCol = this.dragCurrent.col - Math.max(col, endCol);
            const dRow = this.dragCurrent.row - Math.max(row, endRow);

            if (dCol > 0 && dCol >= dRow) {
                // Fill Right
                const destCol = Math.max(col, endCol) + 1;
                const destRow = startRow;
                const destW = dCol; // Number of cols to fill
                const destH = srcH;

                this.streamer.fillData(startCol, startRow, srcW, srcH, destCol, destRow, destW, destH, 'auto');
                this.updateStreamerViewport();
                this.emitFillOptions({
                    sourceCol: startCol,
                    sourceRow: startRow,
                    sourceWidth: srcW,
                    sourceHeight: srcH,
                    targetCol: destCol,
                    targetRow: destRow,
                    targetWidth: destW,
                    targetHeight: destH,
                    canFillSeries: true,
                });

                // Update selection to include filled area
                this.selection.endCol += destW;
                this.notifySelectionChange();
            } else if (dRow > 0) {
                // Fill Down
                const destCol = startCol;
                const destRow = Math.max(row, endRow) + 1;
                const destW = srcW;
                const destH = dRow; // Number of rows to fill

                this.streamer.fillData(startCol, startRow, srcW, srcH, destCol, destRow, destW, destH, 'auto');
                this.updateStreamerViewport();
                this.emitFillOptions({
                    sourceCol: startCol,
                    sourceRow: startRow,
                    sourceWidth: srcW,
                    sourceHeight: srcH,
                    targetCol: destCol,
                    targetRow: destRow,
                    targetWidth: destW,
                    targetHeight: destH,
                    canFillSeries: true,
                });

                // Update selection to include filled area
                this.selection.endRow += destH;
                this.notifySelectionChange();
            }
        }

        this.dragMode = 'none';
        this.dragStart = null;
        this.dragCurrent = null;
        this.requestRender();
    }

    handleDoubleClick = (e: MouseEvent) => {
        if (!this.canvas) return;
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const cell = this.hitTest(x, y);
        if (cell) {
            this.startEditing(cell.col, cell.row);
        }
    }

    // Callbacks are defined at the top

    // Dimensions
    // colCount and rowCount are already defined at the top

    setDimensions(cols: number, rows: number, opts?: { notifyStreamer?: boolean }) {
        if (this.colCount === cols && this.rowCount === rows) return;
        console.log(`[GridController] setDimensions: ${cols}x${rows}`);

        this.colCount = cols;
        this.rowCount = rows;
        this.frozenRowsTop = Math.min(this.frozenRowsTop, this.rowCount);
        this.frozenColsLeft = Math.min(this.frozenColsLeft, this.colCount);

        if (this.onRowCountChange) {
            this.onRowCountChange(rows);
        }

        this.invalidateCache();
        this.requestRender();
        if (this.streamer && opts?.notifyStreamer !== false) {
            this.streamer.setDimensions(cols, rows);
        }
    }

    // requestRender is already defined above





    setStartEditCallback(cb: (col: number, row: number, rect: { x: number, y: number, w: number, h: number }, initialInput?: string, format?: CellFormat) => void) {
        this.onStartEdit = cb;
    }

    startEditing(col: number, row: number, initialInput?: string) {
        if (this.onStartEdit) {
            const rect = this.getRangeScreenRect({
                start: { col, row },
                end: { col, row },
            }) || {
                x: this.getColumnOffset(col) - this.scrollX,
                y: this.getRowOffset(row) - this.scrollY,
                w: this.getColumnWidth(col),
                h: this.getRowHeight(row),
            };

            const format = this.getCellFormat(col, row);
            this.onStartEdit(col, row, rect, initialInput, format);
        }
    }

    setIsEditing(editing: boolean) {
        this.isEditing = editing;
    }

    setHostHandlesUndoRedoShortcuts(next: boolean) {
        this.hostHandlesUndoRedoShortcuts = Boolean(next);
    }

    private toggleSelectionFormat(key: 'bold' | 'italic' | 'underline') {
        if (!this.selection) return;
        if (!this.canFormatSelectionBySheetProtection()) return;
        const current = this.getAuthoringCellFormat(this.selection.col, this.selection.row) || {};
        const enabled = key === 'bold'
            ? current.bold
            : key === 'italic'
                ? current.italic
                : current.underline;
        this.applyFormatToSelection({ [key]: !enabled } as Partial<CellFormat>);
    }

    private executeGridKeyCommand(command: SpreadsheetGridCommandDecision, navStart: number) {
        switch (command.commandId) {
            case 'undo':
                this.streamer?.undo();
                return;
            case 'redo':
                this.streamer?.redo();
                return;
            case 'start-edit':
                if (this.selection) this.startEditing(this.selection.col, this.selection.row);
                return;
            case 'start-edit-with-input':
                if (this.selection) this.startEditing(this.selection.col, this.selection.row, command.inputText);
                return;
            case 'start-autosum':
                if (this.selection) this.startAutoSumFormulaEdit(this.selection.col, this.selection.row);
                return;
            case 'clear-selection':
                this.clearSelectedCells();
                return;
            case 'select-row':
            case 'select-column':
            case 'select-all':
                if (!command.nextSelection) return;
                if (!this.setSelection(command.nextSelection)) return;
                this.notifySelectionChange();
                this.requestRender();
                return;
            case 'toggle-bold':
                this.toggleSelectionFormat('bold');
                return;
            case 'toggle-italic':
                this.toggleSelectionFormat('italic');
                return;
            case 'toggle-underline':
                this.toggleSelectionFormat('underline');
                return;
            case 'open-validation-dropdown':
                this.onValidationDropdownRequest?.();
                return;
            case 'move-selection':
                if (!command.nextSelection) return;
                if (!this.setSelection(command.nextSelection)) return;
                this.notifySelectionChange();
                this.scrollToCell(command.nextSelection.endCol, command.nextSelection.endRow);
                this.requestRender();
                this.navLog(`keydown->scrollToCell (${command.commandId})`, navStart);
                return;
            default:
                return;
        }
    }

    handleKeyDown = (e: KeyboardEvent) => {
        const navStart = performance.now();
        if (e.defaultPrevented) return;

        // Only handle keyboard navigation/edits when the grid is focused.
        // This prevents the grid from hijacking typing or shortcuts elsewhere in the app.
        if (!this.hasFocus) {
            return;
        }

        // Ignore keyboard events that originate inside input/textarea elements (like formula bar).
        // `document.activeElement` can remain on the previous input after the grid has claimed
        // keyboard focus through pointer/selection state, so the event target is the source of truth.
        if (this.isEditableTarget(e.target)) {
            return;
        }

        const command = resolveSpreadsheetGridKeyCommand({
            key: e.key,
            shiftKey: e.shiftKey,
            ctrlKey: e.ctrlKey,
            metaKey: e.metaKey,
            altKey: e.altKey,
        }, {
            hostHandlesUndoRedoShortcuts: this.hostHandlesUndoRedoShortcuts,
            isEditing: this.isEditing,
            selection: this.selection,
            rowCount: this.rowCount,
            colCount: this.colCount,
            visibleRowCount: this.getVisibleRowCount(),
            visibleColCount: this.getVisibleColCount(),
            hasValidationDropdown: this.validationDropdownAvailable,
            sheetProtection: this.getSheetProtectionProjection(),
        });

        if (command.disposition !== 'grid-command') return;
        if (command.preventDefault) e.preventDefault();
        if (command.stopImmediatePropagation) e.stopImmediatePropagation();
        this.executeGridKeyCommand(command, navStart);
    }

    notifySelectionChange() {
        if (this.onSelectionChange) {
            this.onSelectionChange(this.selection);
        }
    }

    private getVisibleRowCount(): number {
        return Math.floor(this.viewportHeight / (GridController.DEFAULT_ROW_HEIGHT * this.zoom));
    }

    private getVisibleColCount(): number {
        return Math.floor(this.viewportWidth / (GridController.DEFAULT_COL_WIDTH * this.zoom));
    }



    // Helper methods for hit testing
    // Cache for binary search
    private colOffsetsCache: number[] | null = null;
    private rowOffsetsCache: number[] | null = null;

    // Debug helper to measure nav latency on main thread
    private navLog(label: string, start: number) {
        const dt = performance.now() - start;
        console.log(`[nav] ${label} ${dt.toFixed(2)}ms`);
    }

    // Debug throttles
    private lastVisLogY = -1;
    private lastVisLogTime = 0;

    private invalidateCache() {
        this.colOffsetsCache = null;
        this.rowOffsetsCache = null;
    }

    private hasUniformRowHeights(): boolean {
        return !this.sharedState && this.rowHeights.size === 0;
    }

    private ensureColOffsets() {
        if (this.colOffsetsCache) return;
        if (this.colCount > GridController.COL_OFFSET_CACHE_LIMIT) {
            // Skip building huge col cache; use on-demand defaults
            this.colOffsetsCache = null;
            return;
        }
        this.colOffsetsCache = new Array(this.colCount + 1);
        let offset = 0;
        for (let i = 0; i < this.colCount; i++) {
            this.colOffsetsCache[i] = offset;
            offset += this.getColumnWidth(i);
        }
        this.colOffsetsCache[this.colCount] = offset;
    }

    private ensureRowOffsets() {
        if (this.rowOffsetsCache) return;
        if (this.hasUniformRowHeights()) return;
        if (this.rowCount > GridController.ROW_OFFSET_CACHE_LIMIT) {
            this.rowOffsetsCache = null;
            return;
        }

        // #region agent log
        console.log('[GridController] ensureRowOffsets: building cache', { rowCount: this.rowCount });
        // #endregion
        this.rowOffsetsCache = new Array(this.rowCount + 1);
        let offset = 0;
        for (let i = 0; i < this.rowCount; i++) {
            const height = this.getRowHeight(i);
            // #region agent log
            if (i > 75 && height === undefined) {
                console.warn('[GridController] ensureRowOffsets: undefined height', { row: i, rowCount: this.rowCount });
            }
            // #endregion
            this.rowOffsetsCache[i] = offset;
            offset += height;
        }
        this.rowOffsetsCache[this.rowCount] = offset;
        // #region agent log
        console.log('[GridController] ensureRowOffsets: cache built', { cacheLength: this.rowOffsetsCache.length, lastOffset: offset });
        // #endregion
    }

    // Override setColumnWidth/setRowHeight to invalidate cache
    setColumnWidth(col: number, width: number, opts?: { displaySize?: boolean }) {
        const w = Math.max(20, opts?.displaySize ? width / this.zoom : width);
        if (this.sharedState && !this.suppressSharedLayoutWrites) {
            this.sharedState.setColWidth(col, w);
        }
        this.colWidths.set(col, w);
        this.invalidateCache();
        this.requestRender();
    }

    setRowHeight(row: number, height: number, opts?: { displaySize?: boolean }) {
        const h = Math.max(20, opts?.displaySize ? height / this.zoom : height);
        if (this.sharedState && !this.suppressSharedLayoutWrites) {
            this.sharedState.setRowHeight(row, h);
        }
        this.rowHeights.set(row, h);
        this.invalidateCache();
        this.requestRender();
    }

    getColumnOffset(col: number): number {
        this.ensureColOffsets();
        if (this.colOffsetsCache) {
            return this.colOffsetsCache[col] ?? 0;
        }
        // Fallback: approximate with default width
        const w = this.getColumnWidth(0) || 100;
        return col * w;
    }

    getRowOffset(row: number): number {
        if (this.hasUniformRowHeights()) {
            return row * GridController.DEFAULT_ROW_HEIGHT * this.zoom;
        }
        this.ensureRowOffsets();
        if (this.rowOffsetsCache) {
            return this.rowOffsetsCache[row] ?? 0;
        }
        // Fallback: approximate with default height
        const h = this.getRowHeight(0) || 24;
        return row * h;
    }

    getTotalWidth(): number {
        this.ensureColOffsets();
        if (this.colOffsetsCache) {
            return this.colOffsetsCache[this.colCount];
        }
        const w = this.getColumnWidth(0) || 100;
        return this.colCount * w;
    }

    getTotalHeight(): number {
        if (this.hasUniformRowHeights()) {
            return this.rowCount * GridController.DEFAULT_ROW_HEIGHT * this.zoom;
        }
        this.ensureRowOffsets();
        if (this.rowOffsetsCache) {
            return this.rowOffsetsCache[this.rowCount];
        }
        const h = this.getRowHeight(0) || 24;
        return this.rowCount * h;
    }

    private clampScrollTargets() {
        const maxY = Math.max(0, this.getRowOffset(this.rowCount - 1));
        const maxX = Math.max(0, this.getColumnOffset(this.colCount - 1));
        this.scrollY = Math.min(this.scrollY, maxY);
        this.scrollX = Math.min(this.scrollX, maxX);
    }

    // Binary search helper
    private binarySearch(offsets: number[], value: number): number {
        let low = 0;
        let high = offsets.length - 2; // Last index is total size, so check up to count-1

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const start = offsets[mid];
            const end = offsets[mid + 1];

            if (value >= start && value < end) {
                return mid;
            } else if (value < start) {
                high = mid - 1;
            } else {
                low = mid + 1;
            }
        }
        return -1;
    }

    private getFrozenWidth(): number {
        let width = 0;
        for (let col = 0; col < this.frozenColsLeft && col < this.colCount; col++) {
            width += this.getColumnWidth(col) || 0;
        }
        return width;
    }

    private getFrozenHeight(): number {
        let height = 0;
        for (let row = 0; row < this.frozenRowsTop && row < this.rowCount; row++) {
            height += this.getRowHeight(row) || 0;
        }
        return height;
    }

    private findColumnAtWorldX(worldX: number): number {
        this.ensureColOffsets();
        if (this.colOffsetsCache) {
            const col = this.binarySearch(this.colOffsetsCache, worldX);
            if (col >= 0) return Math.max(0, Math.min(this.colCount - 1, col));
        }
        const w = this.getColumnWidth(0) || 100;
        return Math.max(0, Math.min(this.colCount - 1, Math.floor(worldX / w)));
    }

    private findRowAtWorldY(worldY: number): number {
        if (this.hasUniformRowHeights()) {
            return Math.max(0, Math.min(this.rowCount - 1, Math.floor(worldY / (GridController.DEFAULT_ROW_HEIGHT * this.zoom))));
        }
        if (this.rowCount <= GridController.ROW_OFFSET_CACHE_LIMIT) {
            this.ensureRowOffsets();
            if (this.rowOffsetsCache) {
                const row = this.binarySearch(this.rowOffsetsCache, worldY);
                if (row >= 0) return Math.max(0, Math.min(this.rowCount - 1, row));
            }
        }
        const h = this.getRowHeight(0) || 24;
        return Math.max(0, Math.min(this.rowCount - 1, Math.floor(worldY / h)));
    }

    public hitTest(x: number, y: number): { col: number, row: number } | null {
        return perf('controller.hitTest', () => {
            const frozenWidth = this.getFrozenWidth();
            const frozenHeight = this.getFrozenHeight();
            if (this.frozenColsLeft > 0 && frozenWidth > 0 && x >= 0 && x < frozenWidth) {
                const col = this.findColumnAtWorldX(x);
                if (this.frozenRowsTop > 0 && frozenHeight > 0 && y >= 0 && y < frozenHeight) {
                    return { col, row: this.findRowAtWorldY(y) };
                }
                return { col, row: this.findRowAtWorldY(y + this.scrollY) };
            }
            // Frozen header rows: map clicks in the pinned region to those rows, regardless of scrollY.
            if (this.frozenRowsTop > 0) {
                let frozenY = 0;
                for (let frozenRow = 0; frozenRow < this.frozenRowsTop && frozenRow < this.rowCount; frozenRow++) {
                    const rowHeight = this.getRowHeight(frozenRow) || 0;
                    const nextFrozenY = frozenY + rowHeight;
                    if (rowHeight > 0 && y >= frozenY && y < nextFrozenY) {
                        // Still compute col in world space (header scrolls horizontally).
                        const worldX = x + this.scrollX;
                        return { col: this.findColumnAtWorldX(worldX), row: frozenRow };
                    }
                    frozenY = nextFrozenY;
                }
            }

            const worldX = x + this.scrollX;
            const worldY = y + this.scrollY;

            const col = this.findColumnAtWorldX(worldX);
            const row = this.findRowAtWorldY(worldY);
            if (col >= 0 && row >= 0) {
                return { col, row };
            }
            return null;
        });
    }

    public getVisibleBounds(): { startCol: number, endCol: number, startRow: number, endRow: number } {
        this.ensureColOffsets();
        if (!this.hasUniformRowHeights() && this.rowCount <= GridController.ROW_OFFSET_CACHE_LIMIT) {
            this.ensureRowOffsets();
        }

        const defaultColW = this.getColumnWidth(0) || 100;
        const defaultRowH = this.hasUniformRowHeights()
            ? GridController.DEFAULT_ROW_HEIGHT * this.zoom
            : (this.getRowHeight(0) || 24);

        const totalW = this.colOffsetsCache ? this.colOffsetsCache[this.colCount] : this.colCount * defaultColW;
        const totalH = this.rowOffsetsCache ? this.rowOffsetsCache[this.rowCount] : this.rowCount * defaultRowH;

        // Clamp scroll to grid bounds before computing visible range
        const clampedX = Math.min(this.scrollX, Math.max(0, totalW - this.viewportWidth));
        const clampedY = Math.min(this.scrollY, Math.max(0, totalH - this.viewportHeight));
        this.scrollX = clampedX;
        this.scrollY = clampedY;

        // World-space bounds for the current viewport
        const startWorldX = clampedX;
        const startWorldY = clampedY;
        const endWorldX = Math.min(clampedX + this.viewportWidth, totalW);
        const endWorldY = Math.min(clampedY + this.viewportHeight, totalH);

        const findIndex = (
            offsets: number[] | null,
            value: number,
            count: number,
            defaultSize: number
        ) => {
            if (offsets) {
                const idx = this.binarySearch(offsets, value);
                if (idx >= 0) return idx;
            }
            return Math.max(0, Math.min(count - 1, Math.floor(value / defaultSize)));
        };

        const startCol = findIndex(this.colOffsetsCache, startWorldX, this.colCount, defaultColW);
        const startRow = findIndex(this.rowOffsetsCache, startWorldY, this.rowCount, defaultRowH);
        const endCol = Math.max(
            startCol,
            findIndex(this.colOffsetsCache, endWorldX, this.colCount, defaultColW)
        );
        const endRow = Math.max(
            startRow,
            findIndex(this.rowOffsetsCache, endWorldY, this.rowCount, defaultRowH)
        );

        // Debug: log when jumping near bottom on huge grids to diagnose header/render mismatches
        if (this.rowCount > 200000) {
            const now = performance.now();
            if (Math.abs(this.scrollY - this.lastVisLogY) > 500000 || (now - this.lastVisLogTime) > 1000) {
                console.log('[visBounds]', {
                    scrollY: this.scrollY,
                    clampedY,
                    totalH,
                    rowCount: this.rowCount,
                    hasRowCache: !!this.rowOffsetsCache,
                    defaultRowH,
                    startRow,
                    endRow,
                    viewportH: this.viewportHeight
                });
                this.lastVisLogY = this.scrollY;
                this.lastVisLogTime = now;
            }
        }

        return { startCol, endCol, startRow, endRow };
    }

    scrollToCell(col: number, row: number) {
        const targetX = this.getColumnOffset(col);
        const targetY = this.getRowOffset(row);
        const cellW = this.getColumnWidth(col);
        const cellH = this.getRowHeight(row);

        let newX = this.scrollX;
        let newY = this.scrollY;

        // Horizontal visibility
        if (targetX < this.scrollX) {
            newX = targetX;
        } else if (targetX + cellW > this.scrollX + this.viewportWidth) {
            newX = targetX + cellW - this.viewportWidth;
        }

        // Vertical visibility
        if (targetY < this.scrollY) {
            newY = targetY;
        } else if (targetY + cellH > this.scrollY + this.viewportHeight) {
            newY = targetY + cellH - this.viewportHeight;
        }

        this.setScroll(newX, newY);
        this.clampScrollTargets();
    }
}
